import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Stack,
  Divider,
  Switch,
  FormGroup,
  Card,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { getPostTemplates } from '../../api/postTemplate.api.js';
import { getAgenticFlows } from '../../api/agenticFlow.api';
import { ExpressionBuilder, buildVariableList } from './ExpressionBuilder';
import { OLLAMA_MODEL_OPTIONS, DEFAULT_OLLAMA_MODEL } from '../../constants/ollamaModels.js';
import { getLangGraphWsUrl } from '../../utils/langgraphConfig.js';

// Execute Flow: flow selector that fetches flows from API
function ExecuteFlowIdSelect({ value, onChange }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    getAgenticFlows({}).then(({ response }) => {
      const list = Array.isArray(response) ? response : (response?.flows ?? response?.data ?? []);
      setFlows(list || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  return (
    <FormControl fullWidth>
      <InputLabel>Target Flow</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label="Target Flow"
        disabled={loading}
      >
        <MenuItem value="">
          <em>Select a flow...</em>
        </MenuItem>
        {flows.map((f) => (
          <MenuItem key={f._id || f.id} value={f._id || f.id}>
            {f.name || 'Untitled Flow'}
          </MenuItem>
        ))}
      </Select>
      {!value && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Required: select the flow to run as subflow
        </Typography>
      )}
    </FormControl>
  );
}

// Helper function to get default arguments for MCP tools
const getToolDefaults = (toolName) => {
  const defaults = {
    database_query: {
      query: 'SELECT * FROM table WHERE id = ?',
      parameters: [],
    },
    file_operations: {
      operation: 'read',
      path: '/path/to/file',
    },
    ai_processor: {
      task: 'process',
      data: '',
    },
    http_request: {
      url: 'https://api.example.com',
      method: 'GET',
      headers: {},
      body: '',
    },
  };
  return defaults[toolName] || {};
};

const getDefaultNodeConfig = (nodeType) => {
  const configs = {
    'ai-agent': {
      model: 'gemini-3.5-flash-lite',
      provider: 'gemini',
      temperature: 0.7,
      maxTokens: 1000,
      systemPrompt: 'You are a helpful AI assistant.',
      tools: [],
      memory: true,
      memoryEnabled: true,
      contextWindow: 'previous_attempts',
      inputFormat: 'mixed',
      outputFormat: 'structured_json',
      stateVariables: ['classification_result', 'ai_inference_history', 'iteration_count'],
    },
    'code-executor': {
      language: 'javascript',
      code: '// Enter your code here\nreturn { result: "Hello World" };',
      timeout: 30000,
      environment: 'node',
    },
    'api-call': {
      method: 'GET',
      url: 'https://api.example.com/endpoint',
      headers: {
        'Content-Type': 'application/json',
      },
      body: '',
      timeout: 10000,
      retries: 3,
      stateVars: ['apiResponse'],
      fallback_strategy: 'fail',
      fallback_data: {},
      simulate: false,
      circuit_breaker: true,
      max_failures: 5,
      circuit_timeout: 300,
    },
    webhook: {
      url: 'https://api.example.com/webhook',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer your-token-here',
      },
      body: '{"message": "Hello from webhook!", "user_id": "{user_id}"}',
      timeout: 30000,
      retries: 3,
      responseVariable: 'webhook_response',
      successStatusCodes: [200, 201, 202],
    },
    http_request: {
      url: 'https://api.example.com/endpoint',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      body: '',
      timeout: 30000,
      retries: 3,
      responseVariable: 'api_response',
      successStatusCodes: [200, 201, 202],
    },
    database: {
      connectionString: 'mongodb://localhost:27017',
      database: 'flowdb',
      collection: 'documents',
      operation: 'find',
      query: '{"status": "active"}',
      projection: '{"_id": 0, "name": 1, "created_at": 1}',
      limit: 10,
      skip: 0,
      sort: '{"created_at": -1}',
      resultVariable: 'db_result',
      errorVariable: 'db_error',
    },
    db_query: {
      connectionString: 'mongodb://localhost:27017',
      database: 'flowdb',
      collection: 'documents',
      operation: 'find',
      query: '{"user_id": "{user_id}"}',
      projection: '{"_id": 0}',
      limit: 10,
      resultVariable: 'query_result',
    },
    db_insert: {
      connectionString: 'mongodb://localhost:27017',
      database: 'flowdb',
      collection: 'documents',
      operation: 'insert_one',
      document: '{"user_id": "{user_id}", "action": "{action}", "timestamp": "{timestamp}"}',
      resultVariable: 'insert_result',
    },
    db_update: {
      connectionString: 'mongodb://localhost:27017',
      database: 'flowdb',
      collection: 'documents',
      operation: 'update_one',
      query: '{"user_id": "{user_id}"}',
      update: '{"$set": {"last_action": "{action}", "updated_at": "{timestamp}"}}',
      resultVariable: 'update_result',
    },
    db_delete: {
      connectionString: 'mongodb://localhost:27017',
      database: 'flowdb',
      collection: 'documents',
      operation: 'delete_one',
      query: '{"user_id": "{user_id}", "action": "old_action"}',
      resultVariable: 'delete_result',
    },
    email: {
      to: '',
      subject: '',
      body: '',
      template: 'default',
      attachments: [],
    },
    sms: {
      to: '',
      message: '',
      provider: 'twilio',
    },
    conditional: {
      condition: 'user_age >= 18 AND is_logged_in == true',
      trueTarget: 'adult_flow',
      falseTarget: 'minor_flow',
    },
    switch: {
      variable: 'user_role',
      cases: [
        { value: 'admin', target: 'admin_dashboard' },
        { value: 'user', target: 'user_dashboard' },
        { value: 'guest', target: 'guest_welcome' },
      ],
      defaultTarget: 'default_flow',
    },
    'decision-tree': {
      rules: [
        { condition: 'user_score >= 90 AND is_verified == true', target: 'premium_flow' },
        { condition: 'user_score >= 70', target: 'standard_flow' },
        { condition: 'exists:user_email', target: 'email_flow' },
      ],
      defaultTarget: 'basic_flow',
    },
    loop: {
      loopType: 'while',
      maxIterations: 10,
      breakCondition: '',
      aggregationStrategy: 'final_result',
      debugMode: true,
      loopBodyNodes: [],
      // Legacy fields for backward compatibility
      type: 'forEach',
      array: 'input.items',
    },
    transform: {
      inputSchema: {},
      outputSchema: {},
      transformation: 'return input;',
    },
    delay: {
      duration: 1000,
      unit: 'milliseconds',
    },
    scheduler: {
      type: 'cron',
      expression: '0 */5 * * * *',
      timezone: 'UTC',
    },
    // Add missing node types with minimal defaults:
    'try-catch': { tryBlock: '', catchBlock: '' },
    filter: { arrayField: '', condition: '' },
    sort: { arrayField: '', sortBy: '', order: 'asc' },
    merge: { sources: [], strategy: 'overwrite' },
    group: { groupBy: '', aggregate: '' },
    chat: { participants: '', initialMessage: '' },
    'message-queue': { queueName: '', payload: '' },
    'voice-call': { to: '', provider: '', script: '' },
    trigger: { eventName: '', payload: '' },
    parallel: {
      branches: '',
      timeout: 30000,
      min_completed: 1,
      aggregation_rules: {},
    },
    join: {
      joinType: 'all',
      sources: '',
      timeout: 30000,
      min_completed: 1,
      aggregation_rules: {},
    },
    router: { routingField: '', routes: '' },
    form: {
      fields: [
        {
          name: 'name', label: 'Name', type: 'text', required: true, default: '',
        },
      ],
      submitTo: 'state', // 'state', 'database', 'api', 'none'
      stateVariable: '', // Which state variable to update
      table: '',
      apiEndpoint: '',
      submitLabel: 'Submit',
      successMessage: 'Form submitted successfully!',
      errorMessage: 'Submission failed.',
      // Dynamic form generation settings
      dynamicGeneration: false,
      dynamicSource: '', // State variable containing JSON array of form fields
      aiGenerated: false,
      contextBased: false,
      // State update settings
      updateState: true,
      stateMapping: {}, // Map form fields to state properties
    },
    'social-post': {
      templateId: '',
      content: '',
      platforms: ['facebook', 'twitter', 'linkedin'],
      image: '',
      scheduledAt: '',
    },
    'execute-flow': {
      flowId: '',
      outputVariable: 'subflow_result',
      inputMapping: 'pass_through',
    },
    'mcp-tool': {
      serverUrl: getLangGraphWsUrl('/api/v1/mcp/ws'),
      toolName: '',
      toolArguments: {},
      timeout: 30000,
      retries: 3,
      authentication: 'none',
      authToken: '',
      serverId: 'standalone-mcp-server',
      availableTools: [
        'database_query',
        'file_operations',
        'ai_processor',
        'http_request',
      ],
    },
    policy: {
      mode: 'design', // 'design' = upload & generate, 'existing' = use saved model
      policy_model_id: '',
      collection: 'policy_docs',
      model_name: '',
      file_path: '',
      upload_status: '',
      chunks_added: 0,
      output_to_context: true,
      output_variable: 'policy_model',
      run_query: false,
      // Legacy inline fallback
      input_mode: 'text',
      policy_text: '',
      policy_url: '',
      extraction_prompt: '',
    },
    rag: {
      rag_mode: 'query',
      collection_name: 'documents',
      query: '',
      max_results: 5,
      similarity_threshold: 0.7,
      chunk_size: 1000,
      chunk_overlap: 200,
      // Add consistent model configuration like AI Agent
      model: 'gemini-3.5-flash-lite',
      provider: 'gemini',
      temperature: 0.7,
      maxTokens: 1000,
      systemPrompt: 'You are a helpful AI assistant that answers questions based on the provided document context.',
    },
    tool: {
      tool_name: 'calculator',
      tool_params: {
        expression: '2 + 2 * 3',
      },
      timeout: 30000,
      retries: 3,
    },
    memory: {
      operation: 'store', // 'store', 'retrieve', 'clear'
      key: 'default',
      data: {},
    },
    aggregate: {
      aggregate_type: 'sum', // 'sum', 'count', 'average', 'max', 'min', 'concat'
      field: 'value',
      input_data: [],
    },
  };
  return configs[nodeType] || {};
};

function NodeConfigDialog({
  open, onClose, node, nodes, onSave,
}) {
  const [config, setConfig] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Add state for available models
  const [availableModels, setAvailableModels] = useState([]);
  const [postTemplates, setPostTemplates] = useState([]);
  const [platforms, setPlatforms] = useState(['facebook', 'twitter', 'linkedin', 'instagram']);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [formBuilderForms, setFormBuilderForms] = useState([]);
  const [policyModels, setPolicyModels] = useState([]);
  const [policyUploading, setPolicyUploading] = useState(false);

  // Function to update configuration
  const updateConfig = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Function to handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('collection', config.collection || 'documents');

      const response = await fetch('/api/v1/rag/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update config with upload result
          updateConfig('file_path', file.name);
          updateConfig('file_type', file.name.split('.').pop().toLowerCase());
          updateConfig('upload_status', 'completed');
          updateConfig('chunks_added', result.chunks_added);

          setUploadProgress(100);
          alert(`✅ Successfully uploaded ${file.name} with ${result.chunks_added} chunks!`);
        } else {
          throw new Error(result.error || 'Upload failed');
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadFile(null);
    }
  };

  // Fetch tenant LLM keys and filter available models
  useEffect(() => {
    async function fetchTenantModels() {
      try {
        const tenantId = localStorage.getItem('tenant_id');

        if (!tenantId) {
          setAvailableModels([...OLLAMA_MODEL_OPTIONS]);
          return;
        }
        const tkn = localStorage.getItem('tkn');

        const res = await fetch(`/api/v1/tenants/${tenantId}/llm-keys`, {
          credentials: 'include',
          headers: tkn
            ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
            : { 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          const data = await res.json();
          // Only include models for which a key or url is set, or Ollama
          const models = [
            { value: 'openai', label: 'OpenAI', provider: 'openai' },
            { value: 'llama4', label: 'LLaMA 4', provider: 'llama-api' },
            ...OLLAMA_MODEL_OPTIONS,
            { value: 'deepseek', label: 'DeepSeek', provider: 'deepseek' },
            { value: 'mixtral', label: 'Mixtral', provider: 'mistral' },
            { value: 'openaa', label: 'OpenAA', provider: 'openaa' },
            { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite', provider: 'gemini' },
            { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', provider: 'gemini' },
            { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', provider: 'gemini' },
            { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', provider: 'gemini' },
            { value: 'gemini-pro', label: 'Gemini Pro', provider: 'gemini' },
            { value: 'anthropic', label: 'Anthropic', provider: 'anthropic' },
            { value: 'azure', label: 'Azure', provider: 'azure' },
            { value: 'mistral', label: 'Mistral', provider: 'mistral' },
            { value: 'cohere', label: 'Cohere', provider: 'cohere' },
            { value: 'together', label: 'Together', provider: 'together' },
            { value: 'custom', label: 'Custom', provider: 'custom' },
          ].filter(
            (m) => m.provider === 'ollama' // Always include Ollama
              || m.provider === 'gemini' && (data.gemini || data.gemini_url) // Gemini when tenant has key
              || data[m.value]
              || data[`${m.value}_url`],
          );
          setAvailableModels(models);
        } else {
          setAvailableModels([...OLLAMA_MODEL_OPTIONS]);
        }
      } catch (error) {
        setAvailableModels([...OLLAMA_MODEL_OPTIONS]);
      }
    }
    if (open && (node?.type === 'ai-agent' || node?.type === 'rag')) fetchTenantModels();
  }, [open, node]);

  useEffect(() => {
    if (node) {
      const nodeConfig = node.data?.config || getDefaultNodeConfig(node.type);
      // Ensure config has required properties
      if (nodeConfig && node.type === 'form') {
        nodeConfig.fields = nodeConfig.fields || [];
        nodeConfig.submitTo = nodeConfig.submitTo || 'state';
        nodeConfig.stateVariable = nodeConfig.stateVariable || '';
        nodeConfig.stateMapping = nodeConfig.stateMapping || {};
        nodeConfig.dynamicGeneration = nodeConfig.dynamicGeneration || false;
        nodeConfig.aiGenerated = nodeConfig.aiGenerated || false;
        nodeConfig.contextBased = nodeConfig.contextBased || false;
        nodeConfig.updateState = nodeConfig.updateState !== false;
      }
      setConfig(nodeConfig);
    }
  }, [node]);

  useEffect(() => {
    if (open && node?.type === 'social-post') {
      getPostTemplates().then(setPostTemplates).catch(() => setPostTemplates([]));
    }
  }, [open, node]);

  useEffect(() => {
    if (open && node?.type === 'policy') {
      fetch('/api/v1/policy/models')
        .then((r) => r.ok ? r.json() : { models: [] })
        .then((d) => setPolicyModels(d.models || []))
        .catch(() => setPolicyModels([]));
    }
  }, [open, node?.type]);

  // Load FormBuilder forms for form nodes
  useEffect(() => {
    async function fetchFormBuilderForms() {
      try {
        const tkn = localStorage.getItem('tkn');
        const res = await fetch('/api/v1/flows/formbuilder-forms', {
          credentials: 'include',
          headers: tkn
            ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
            : { 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          const forms = await res.json();
          setFormBuilderForms(forms);
        } else {
          console.error('Failed to fetch FormBuilder forms');
          setFormBuilderForms([]);
        }
      } catch (error) {
        console.error('Error fetching FormBuilder forms:', error);
        setFormBuilderForms([]);
      }
    }

    if (open && node?.type === 'form') {
      fetchFormBuilderForms();
    }
  }, [open, node]);

  const handleSave = () => {
    // Ensure provider is always set for ai-agent and rag nodes
    let configToSave = { ...config };
    if ((node.type === 'ai-agent' || node.type === 'rag') && !configToSave.provider) {
      configToSave.provider = 'ollama';
    }
    onSave(node.id, configToSave);
    onClose();
  };

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerateCode = async () => {
    if (!config.codeDescription) return;

    setGeneratingCode(true);
    try {
      const response = await fetch('/api/v1/ai/generate-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: config.codeDescription,
          language: config.language || 'python',
          context: {
            nodeType: node.type,
            stateVariables: config.stateVars || [],
            flowContext: 'code-executor',
          },
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.code) {
          handleConfigChange('code', result.code);
        } else {
          throw new Error(result.error || 'Failed to generate code');
        }
      } else {
        throw new Error('Failed to generate code');
      }
    } catch (error) {
      console.error('Code generation error:', error);
      alert(`❌ Code generation failed: ${error.message}`);
    } finally {
      setGeneratingCode(false);
    }
  };

  const renderConfigFields = () => {
    if (!node) return null;

    const { type } = node;

    switch (type) {
      case 'ai-agent':
        return (
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                label="Model"
                value={config.model || ''}
                onChange={(e) => {
                  const selected = availableModels.find((m) => m.value === e.target.value);
                  handleConfigChange('model', e.target.value);
                  if (selected && selected.provider) {
                    handleConfigChange('provider', selected.provider);
                  }
                }}
                size="medium"
              >
                {availableModels.length === 0 && (
                  <MenuItem value="" disabled>
                    No models configured for this tenant
                  </MenuItem>
                )}
                {availableModels.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                    {' '}
                    <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 8 }}>
                      (
                      {m.provider}
                      )
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography gutterBottom>
                Temperature:
                {' '}
                {config.temperature || 0.7}
              </Typography>
              <Slider
                value={config.temperature || 0.7}
                onChange={(_, value) => handleConfigChange('temperature', value)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' },
                ]}
                sx={{ mt: 1 }}
              />
            </Box>

            <TextField
              label="Max Tokens"
              type="number"
              value={config.maxTokens || 1000}
              onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
              fullWidth
              size="medium"
              inputProps={{ min: 1, max: 8000 }}
            />

            <TextField
              label="System Prompt"
              multiline
              minRows={4}
              maxRows={8}
              value={config.systemPrompt || ''}
              onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
              fullWidth
              placeholder="Enter the system prompt for the AI agent..."
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.9rem',
                  lineHeight: 1.4,
                },
              }}
            />

            {/* Memory and Context Settings */}
            <Card variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'info.dark' }}>
                🧠 Memory & Context Settings
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={config.memoryEnabled !== false}
                    onChange={(e) => handleConfigChange('memoryEnabled', e.target.checked)}
                  />
                )}
                label="Enable Memory"
                sx={{ mb: 2 }}
              />

              {config.memoryEnabled !== false && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Context Window</InputLabel>
                  <Select
                    value={config.contextWindow || 'previous_attempts'}
                    onChange={(e) => handleConfigChange('contextWindow', e.target.value)}
                    label="Context Window"
                  >
                    <MenuItem value="previous_attempts">Previous Attempts</MenuItem>
                    <MenuItem value="all_history">All History</MenuItem>
                    <MenuItem value="recent_5">Recent 5 Interactions</MenuItem>
                    <MenuItem value="recent_10">Recent 10 Interactions</MenuItem>
                    <MenuItem value="session_only">Session Only</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Card>

            {/* Input/Output Format Settings */}
            <Card variant="outlined" sx={{ p: 2, bgcolor: 'success.lighter' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                📥📤 Input/Output Format
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Input Format</InputLabel>
                <Select
                  value={config.inputFormat || 'mixed'}
                  onChange={(e) => handleConfigChange('inputFormat', e.target.value)}
                  label="Input Format"
                >
                  <MenuItem value="mixed">Mixed (JSON + Text)</MenuItem>
                  <MenuItem value="json">JSON Only</MenuItem>
                  <MenuItem value="text">Text Only</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Output Format</InputLabel>
                <Select
                  value={config.outputFormat || 'structured_json'}
                  onChange={(e) => handleConfigChange('outputFormat', e.target.value)}
                  label="Output Format"
                >
                  <MenuItem value="structured_json">Structured JSON</MenuItem>
                  <MenuItem value="text">Plain Text</MenuItem>
                  <MenuItem value="json">Raw JSON</MenuItem>
                </Select>
              </FormControl>
            </Card>

            <TextField
              label="State Variables (comma separated)"
              value={(config.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                handleConfigChange('stateVars', value);
              }}
              fullWidth
              placeholder="e.g. ai_response, model_used, provider, classification_result, ai_inference_history"
              helperText="State variables to store AI response. These will be available to subsequent nodes."
            />

            {/* AI Agent Configuration Summary */}
            <Box sx={{
              p: 2,
              bgcolor: 'primary.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.light',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🤖 AI Agent Configuration Summary:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                Model:
                {' '}
                {config.model || 'gemini-3.5-flash-lite'}
                {' '}
                | Temperature:
                {' '}
                {config.temperature || 0.7}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                Memory:
                {' '}
                {config.memoryEnabled !== false ? 'Enabled' : 'Disabled'}
                {' '}
                | Context:
                {' '}
                {config.contextWindow || 'previous_attempts'}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                Input:
                {' '}
                {config.inputFormat || 'mixed'}
                {' '}
                | Output:
                {' '}
                {config.outputFormat || 'structured_json'}
              </Typography>
              <Typography variant="caption" display="block">
                Max Tokens:
                {' '}
                {config.maxTokens || 1000}
                {' '}
                | State Variables:
                {' '}
                {(config.stateVars || []).length}
              </Typography>
            </Box>
          </Stack>
        );
      case 'code-executor':
        return (
          <Stack spacing={3}>
            <TextField
              label="Language"
              select
              value={config.language || 'javascript'}
              onChange={(e) => handleConfigChange('language', e.target.value)}
              fullWidth
              size="medium"
            >
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="typescript">TypeScript</MenuItem>
            </TextField>

            {/* AI Code Generation Section */}
            <Card variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'info.dark' }}>
                🤖 AI Code Generation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Describe what you want the code to do, and the AI will generate it for you.
              </Typography>

              <TextField
                label="Code Description/Comment"
                multiline
                minRows={3}
                maxRows={6}
                value={config.codeDescription || ''}
                onChange={(e) => handleConfigChange('codeDescription', e.target.value)}
                fullWidth
                placeholder="Describe what you want the code to do... e.g., 'Create a function that processes the AI response and extracts key information'"
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleGenerateCode()}
                  disabled={!config.codeDescription || generatingCode}
                  startIcon={generatingCode ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                >
                  {generatingCode ? 'Generating...' : 'Generate Code'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleConfigChange('codeDescription', '')}
                  startIcon={<ClearIcon />}
                  disabled={generatingCode}
                >
                  Clear
                </Button>
              </Stack>
            </Card>

            {/* Manual Code Section */}
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                ✏️ Manual Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Write your code manually or use the AI-generated code above.
              </Typography>

              <TextField
                label="Code"
                multiline
                minRows={8}
                maxRows={15}
                value={config.code || ''}
                onChange={(e) => handleConfigChange('code', e.target.value)}
                fullWidth
                placeholder="// Enter your code here or use AI generation above..."
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                  },
                }}
              />
            </Card>

            <TextField
              label="Timeout (ms)"
              type="number"
              value={config.timeout || 30000}
              onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
              fullWidth
              size="medium"
              inputProps={{ min: 1000, max: 300000, step: 1000 }}
              helperText="Execution timeout in milliseconds"
            />

            <TextField
              label="State Variables (comma separated)"
              value={(config.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                handleConfigChange('stateVars', value);
              }}
              fullWidth
              placeholder="e.g. result, processed_data, output"
              helperText="State variables to store code execution result. These will be available to subsequent nodes."
            />
          </Stack>
        );

      case 'api-call':
        return (
          <Stack spacing={3}>
            <TextField
              label="Method"
              select
              value={config.method || 'GET'}
              onChange={(e) => handleConfigChange('method', e.target.value)}
              fullWidth
              size="medium"
            >
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
            </TextField>

            <TextField
              label="URL"
              value={config.url || ''}
              onChange={(e) => handleConfigChange('url', e.target.value)}
              fullWidth
              size="medium"
              placeholder="https://api.example.com/endpoint"
              helperText="The full URL for the API endpoint. Use {{variable}} for substitution."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <ExpressionBuilder
                      value={config.url}
                      onChange={(v) => handleConfigChange('url', v)}
                      variables={buildVariableList(nodes, {})}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Headers
              </Typography>
              <TextField
                label="Headers (JSON)"
                multiline
                minRows={4}
                maxRows={8}
                value={JSON.stringify(config.headers || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    if (parsed && typeof parsed === 'object') {
                      handleConfigChange('headers', parsed);
                    }
                  } catch (err) {
                    // Keep the raw text for now, user can fix JSON syntax
                    console.warn('Invalid JSON in headers:', err);
                  }
                }}
                onBlur={(e) => {
                  // Validate JSON on blur
                  try {
                    JSON.parse(e.target.value);
                  } catch (err) {
                    console.warn('Headers JSON is invalid:', err.message);
                  }
                }}
                fullWidth
                placeholder='{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer token"\n}'
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                  },
                  '& .MuiInputBase-root.Mui-error': {
                    borderColor: 'error.main',
                  },
                }}
                helperText="Request headers in JSON format. Use valid JSON syntax."
                error={(() => {
                  try {
                    JSON.parse(JSON.stringify(config.headers || {}, null, 2));
                    return false;
                  } catch {
                    return true;
                  }
                })()}
              />
              <Box sx={{
                mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap',
              }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const currentHeaders = config.headers || {};
                    handleConfigChange('headers', {
                      ...currentHeaders,
                      'Content-Type': 'application/json',
                    });
                  }}
                >
                  Add JSON Header
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const currentHeaders = config.headers || {};
                    handleConfigChange('headers', {
                      ...currentHeaders,
                      Authorization: 'Bearer YOUR_TOKEN',
                    });
                  }}
                >
                  Add Auth Header
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const currentHeaders = config.headers || {};
                    handleConfigChange('headers', {
                      ...currentHeaders,
                      Accept: 'application/json',
                    });
                  }}
                >
                  Add Accept Header
                </Button>
              </Box>
            </Box>

            <TextField
              label="Request Body"
              multiline
              minRows={4}
              maxRows={8}
              value={config.body || ''}
              onChange={(e) => handleConfigChange('body', e.target.value)}
              fullWidth
              placeholder="Request body content (JSON, form data, etc.)"
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.9rem',
                  lineHeight: 1.4,
                },
              }}
              helperText="Request body content"
            />

            <TextField
              label="Timeout (ms)"
              type="number"
              value={config.timeout || 10000}
              onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
              fullWidth
              size="medium"
              inputProps={{ min: 1000, max: 60000, step: 1000 }}
              helperText="Request timeout in milliseconds"
            />

            <TextField
              label="Retries"
              type="number"
              value={config.retries || 3}
              onChange={(e) => handleConfigChange('retries', parseInt(e.target.value))}
              fullWidth
              size="medium"
              inputProps={{ min: 0, max: 10, step: 1 }}
              helperText="Number of retry attempts"
            />

            <TextField
              label="State Variables (comma separated)"
              value={(config.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                handleConfigChange('stateVars', value);
              }}
              fullWidth
              placeholder="e.g. apiResponse, status, data"
              helperText="State variables to store API response. These will be available to subsequent nodes."
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              Error Handling & Fallback
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Fallback Strategy</InputLabel>
              <Select
                value={config.fallback_strategy || 'fail'}
                onChange={(e) => handleConfigChange('fallback_strategy', e.target.value)}
                label="Fallback Strategy"
              >
                <MenuItem value="fail">Fail (Stop flow on error)</MenuItem>
                <MenuItem value="continue">Continue (Use fallback data)</MenuItem>
                <MenuItem value="retry_later">Retry Later (Mark for retry)</MenuItem>
              </Select>
            </FormControl>

            {config.fallback_strategy === 'continue' && (
              <TextField
                label="Fallback Data (JSON)"
                multiline
                minRows={3}
                maxRows={6}
                value={JSON.stringify(config.fallback_data || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    if (parsed && typeof parsed === 'object') {
                      handleConfigChange('fallback_data', parsed);
                    }
                  } catch (err) {
                    console.warn('Invalid JSON in fallback data:', err);
                  }
                }}
                fullWidth
                placeholder='{\n  "message": "Service temporarily unavailable",\n  "data": []\n}'
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.85rem',
                  },
                }}
                helperText="Default data to use when API call fails"
              />
            )}

            <FormControlLabel
              control={(
                <Switch
                  checked={config.simulate || false}
                  onChange={(e) => handleConfigChange('simulate', e.target.checked)}
                />
              )}
              label="Simulation Mode (Use mock responses for testing)"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={config.circuit_breaker !== false}
                  onChange={(e) => handleConfigChange('circuit_breaker', e.target.checked)}
                />
              )}
              label="Circuit Breaker (Prevent cascading failures)"
            />

            {config.circuit_breaker !== false && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Max Failures"
                  type="number"
                  value={config.max_failures || 5}
                  onChange={(e) => handleConfigChange('max_failures', parseInt(e.target.value))}
                  size="small"
                  inputProps={{ min: 1, max: 20, step: 1 }}
                  helperText="Failures before circuit opens"
                />
                <TextField
                  label="Circuit Timeout (seconds)"
                  type="number"
                  value={config.circuit_timeout || 300}
                  onChange={(e) => handleConfigChange('circuit_timeout', parseInt(e.target.value))}
                  size="small"
                  inputProps={{ min: 60, max: 3600, step: 60 }}
                  helperText="How long to keep circuit open"
                />
              </Stack>
            )}
          </Stack>
        );

      case 'conditional':
        return (
          <Stack spacing={3}>
            <TextField
              label="Condition (JavaScript)"
              value={config.condition || ''}
              onChange={(e) => handleConfigChange('condition', e.target.value)}
              fullWidth
              size="medium"
              placeholder="e.g., input.value > 0"
              helperText="JavaScript expression that evaluates to true/false"
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                  fontSize: '0.9rem',
                },
              }}
            />

            <TextField
              label="True Branch Label"
              value={config.trueLabel || 'Yes'}
              onChange={(e) => handleConfigChange('trueLabel', e.target.value)}
              fullWidth
              size="medium"
              helperText="Label for the true path"
            />

            <TextField
              label="False Branch Label"
              value={config.falseLabel || 'No'}
              onChange={(e) => handleConfigChange('falseLabel', e.target.value)}
              fullWidth
              size="medium"
              helperText="Label for the false path"
            />
          </Stack>
        );
      case 'policy':
        return (() => {
          const handlePolicyUpload = async (file) => {
            if (!file) return;
            setPolicyUploading(true);
            try {
              const formData = new FormData();
              formData.append('file', file);
              formData.append('collection', config.collection || 'policy_docs');
              const uploadRes = await fetch('/api/v1/rag/upload', { method: 'POST', body: formData, credentials: 'include' });
              const uploadData = await uploadRes.json();
              if (!uploadRes.ok || !uploadData.success) throw new Error(uploadData.error || 'Upload failed');
              const genRes = await fetch('/api/v1/policy/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
                credentials: 'include',
                body: JSON.stringify({
                  document_id: file.name,
                  collection: config.collection || 'policy_docs',
                  model_name: config.model_name || file.name.replace(/\.[^.]+$/, '') || 'Policy Model',
                  description: `Policy model from ${file.name}`,
                }),
              });
              const genData = await genRes.json();
              if (!genRes.ok) throw new Error(typeof genData.detail === 'string' ? genData.detail : Array.isArray(genData.detail) ? genData.detail.map((d) => d.msg || d).join(', ') : JSON.stringify(genData.detail || 'Policy generation failed'));
              updateConfig('policy_model_id', genData.model_id);
              updateConfig('file_path', file.name);
              updateConfig('upload_status', 'completed');
              updateConfig('chunks_added', uploadData.chunks_added || 0);
              setPolicyModels((prev) => (genData.model ? [...prev, genData.model] : prev));
              alert(`Policy model created: ${genData.model?.name || genData.model_id}`);
            } catch (e) {
              alert(`Policy upload failed: ${e.message}`);
            } finally {
              setPolicyUploading(false);
            }
          };
          return (
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Design-time mode</InputLabel>
                <Select value={config.mode || 'design'} onChange={(e) => updateConfig('mode', e.target.value)} label="Design-time mode">
                  <MenuItem value="design">Upload & generate model</MenuItem>
                  <MenuItem value="existing">Use existing policy model</MenuItem>
                </Select>
              </FormControl>
              {(config.mode || 'design') === 'design' && (
                <>
                  <Typography variant="subtitle2">Upload policy document (PDF, DOCX, TXT)</Typography>
                  <Box sx={{ p: 2, border: '2px dashed #e0e0e0', borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                    <input
                      id="policy-file-input"
                      type="file"
                      accept=".pdf,.docx,.txt"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handlePolicyUpload(f);
                        e.target.value = '';
                      }}
                    />
                    <Button variant="outlined" component="span" onClick={() => document.getElementById('policy-file-input')?.click()} disabled={policyUploading}>
                      {policyUploading ? 'Processing...' : 'Select file & generate model'}
                    </Button>
                    {config.upload_status === 'completed' && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                        ✅ {config.file_path} → model saved
                      </Typography>
                    )}
                  </Box>
                  <TextField fullWidth label="Model name" value={config.model_name || ''} onChange={(e) => updateConfig('model_name', e.target.value)} placeholder="e.g. Return Policy" helperText="Used when generating new model" />
                </>
              )}
              {(config.mode || 'design') === 'existing' && (
                <FormControl fullWidth>
                  <InputLabel>Policy model</InputLabel>
                  <Select value={config.policy_model_id || ''} onChange={(e) => updateConfig('policy_model_id', e.target.value)} label="Policy model">
                    <MenuItem value="">Select a model...</MenuItem>
                    {policyModels.map((m) => (
                      <MenuItem key={m.id || m._id} value={m.id || m._id}>
                        {m.name || m.id || m._id}
                      </MenuItem>
                    ))}
                  </Select>
                  {policyModels.length === 0 && <Typography variant="caption" color="text.secondary">No policy models. Upload one in &quot;Upload & generate&quot; mode first.</Typography>}
                </FormControl>
              )}
              <FormControlLabel control={<Switch checked={config.output_to_context !== false} onChange={(e) => updateConfig('output_to_context', e.target.checked)} />} label="Output to AI context (for AI Agent nodes)" />
              <FormControlLabel control={<Switch checked={config.run_query === true} onChange={(e) => updateConfig('run_query', e.target.checked)} />} label="Run policy query at runtime (with state inputs)" />
              <TextField fullWidth label="Output variable" value={config.output_variable || 'policy_model'} onChange={(e) => updateConfig('output_variable', e.target.value)} helperText="Downstream nodes read state.variables[policy_model].policy_conditions, .policy_limits" />
            </Stack>
          );
        })();

      case 'rag':
        return (
          <Box>
            {/* RAG Mode Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>RAG Mode</InputLabel>
              <Select
                value={config.rag_mode || 'query'}
                onChange={(e) => updateConfig('rag_mode', e.target.value)}
                label="RAG Mode"
              >
                <MenuItem value="query">🔍 Query Documents</MenuItem>
                <MenuItem value="upload">📤 Upload Documents</MenuItem>
              </Select>
            </FormControl>

            {/* Collection Name */}
            <TextField
              fullWidth
              label="Collection Name"
              value={config.collection || 'documents'}
              onChange={(e) => updateConfig('collection', e.target.value)}
              sx={{ mb: 2 }}
              helperText="ChromaDB collection name"
            />

            {/* Upload Mode Configuration */}
            {config.rag_mode === 'upload' && (
              <>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  📤 Upload Settings
                </Typography>

                {/* File Upload Section */}
                <Box sx={{
                  mb: 3, p: 2, border: '2px dashed #e0e0e0', borderRadius: 2, bgcolor: '#fafafa',
                }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    📁 File Upload
                  </Typography>

                  {!uploadFile && !config.file_path && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                      onClick={() => document.getElementById('rag-file-input').click()}
                    >
                      <Typography variant="body2" color="text.secondary">
                        📎 Click to select file or drag & drop
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supports: PDF, DOCX, TXT, JSON, CSV
                      </Typography>
                    </Box>
                  )}

                  {(uploadFile || config.file_path) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2">
                        📄
                        {' '}
                        {uploadFile?.name || config.file_path}
                      </Typography>
                      {config.upload_status === 'completed' && (
                        <Typography variant="caption" color="success.main">
                          ✅ Uploaded (
                          {config.chunks_added}
                          {' '}
                          chunks)
                        </Typography>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setUploadFile(null);
                          updateConfig('file_path', '');
                          updateConfig('upload_status', '');
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}

                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption">Uploading...</Typography>
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                        >
                          <div style={{
                            width: `${uploadProgress}%`,
                            height: '100%',
                            backgroundColor: '#1976d2',
                            transition: 'width 0.3s ease',
                          }}
                          />
                        </div>
                      </Box>
                    </Box>
                  )}

                  <input
                    id="rag-file-input"
                    type="file"
                    style={{ display: 'none' }}
                    accept=".txt,.pdf,.docx,.json,.csv,.md"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadFile(file);
                        // Auto-upload when file is selected
                        handleFileUpload(file);
                      }
                    }}
                  />

                  {uploadFile && !uploading && config.upload_status !== 'completed' && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={() => handleFileUpload(uploadFile)}
                    >
                      Upload File
                    </Button>
                  )}
                </Box>

                {/* Alternative: Manual File Path */}
                <TextField
                  fullWidth
                  label="Or Enter File Path Manually"
                  value={config.file_path || ''}
                  onChange={(e) => updateConfig('file_path', e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="/path/to/document.pdf"
                  helperText="Alternative to file upload"
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>File Type</InputLabel>
                  <Select
                    value={config.file_type || 'text'}
                    onChange={(e) => updateConfig('file_type', e.target.value)}
                    label="File Type"
                  >
                    <MenuItem value="text">📄 Text</MenuItem>
                    <MenuItem value="pdf">📕 PDF</MenuItem>
                    <MenuItem value="docx">📘 Word</MenuItem>
                    <MenuItem value="csv">📊 CSV</MenuItem>
                    <MenuItem value="json">🔧 JSON</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2,
                }}
                >
                  <TextField
                    type="number"
                    label="Chunk Size"
                    value={config.chunk_size || 1000}
                    onChange={(e) => updateConfig('chunk_size', parseInt(e.target.value))}
                    inputProps={{ min: 100, max: 5000 }}
                    helperText="Characters per chunk"
                  />
                  <TextField
                    type="number"
                    label="Chunk Overlap"
                    value={config.chunk_overlap || 200}
                    onChange={(e) => updateConfig('chunk_overlap', parseInt(e.target.value))}
                    inputProps={{ min: 0, max: 1000 }}
                    helperText="Overlap between chunks"
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Direct Content (Optional)"
                  value={config.file_content || ''}
                  onChange={(e) => updateConfig('file_content', e.target.value)}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                  placeholder="Or paste text content here..."
                  helperText="Alternative to file upload/path"
                />
              </>
            )}

            {/* Query Mode Configuration */}
            {config.rag_mode === 'query' && (
              <>
                <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                  🔍 Query Settings
                </Typography>

                {/* LLM Provider and Model Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    label="Model"
                    value={config.model || ''}
                    onChange={(e) => {
                      const selected = availableModels.find((m) => m.value === e.target.value);
                      updateConfig('model', e.target.value);
                      if (selected && selected.provider) {
                        updateConfig('provider', selected.provider);
                      }
                    }}
                    size="medium"
                  >
                    {availableModels.length === 0 && (
                      <MenuItem value="" disabled>
                        No models configured for this tenant
                      </MenuItem>
                    )}
                    {availableModels.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                        {' '}
                        <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 8 }}>
                          (
                          {m.provider}
                          )
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Typography gutterBottom>
                    Temperature:
                    {' '}
                    {config.temperature || 0.7}
                  </Typography>
                  <Slider
                    value={config.temperature || 0.7}
                    onChange={(_, value) => updateConfig('temperature', value)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 0.5, label: '0.5' },
                      { value: 1, label: '1' },
                    ]}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <TextField
                  label="Max Tokens"
                  type="number"
                  value={config.maxTokens || 1000}
                  onChange={(e) => updateConfig('maxTokens', parseInt(e.target.value))}
                  fullWidth
                  size="medium"
                  inputProps={{ min: 1, max: 8000 }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="System Prompt"
                  multiline
                  minRows={3}
                  maxRows={6}
                  value={config.systemPrompt || ''}
                  onChange={(e) => updateConfig('systemPrompt', e.target.value)}
                  fullWidth
                  placeholder="Enter the system prompt for the RAG assistant..."
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-root': {
                      fontSize: '0.9rem',
                      lineHeight: 1.4,
                    },
                  }}
                />

                <Box sx={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2,
                }}
                >
                  <TextField
                    type="number"
                    label="Top K Results"
                    value={config.top_k || 5}
                    onChange={(e) => updateConfig('top_k', parseInt(e.target.value))}
                    inputProps={{ min: 1, max: 20 }}
                    helperText="Number of results"
                  />
                  <TextField
                    type="number"
                    label="Similarity Threshold"
                    value={config.similarity_threshold || 0.0}
                    onChange={(e) => updateConfig('similarity_threshold', parseFloat(e.target.value))}
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    helperText="Min similarity (0.0-1.0)"
                  />
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Response Format</InputLabel>
                  <Select
                    value={config.response_format || 'context_with_query'}
                    onChange={(e) => updateConfig('response_format', e.target.value)}
                    label="Response Format"
                  >
                    <MenuItem value="context_with_query">🔗 Context + Query</MenuItem>
                    <MenuItem value="context_only">📝 Context Only</MenuItem>
                    <MenuItem value="context_with_metadata">📋 Context + Metadata</MenuItem>
                    <MenuItem value="structured">🏗️ Structured JSON</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.include_metadata !== false}
                      onChange={(e) => updateConfig('include_metadata', e.target.checked)}
                    />
                  )}
                  label="Include Metadata"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Question / Query"
                  value={config.query || ''}
                  onChange={(e) => updateConfig('query', e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="Ask a question about your documents..."
                  helperText="This will be sent to the /answer API to get an LLM-generated answer using your document context."
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  disabled={!config.query || uploading}
                  onClick={async () => {
                    // Call the /api/v1/rag/answer endpoint
                    try {
                      const res = await fetch('/api/v1/rag/answer', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          query: config.query,
                          collection: config.collection || 'documents',
                          top_k: config.top_k || 5,
                          llm: config.provider || 'gemini',
                          model: config.model || 'gemini-3.5-flash-lite',
                          temperature: config.temperature || 0.7,
                          max_tokens: config.maxTokens || 1000,
                        }),
                      });
                      const data = await res.json();
                      if (data.success) {
                        // Prefer 'answer' if present, else fallback to 'data'
                        updateConfig('rag_answer', data.answer ?? data.data ?? '');
                        updateConfig('rag_context_used', data.context_used || []);
                        alert('✅ Answer generated!');
                      } else {
                        alert(`❌ ${data.error || 'Failed to get answer'}`);
                      }
                    } catch (err) {
                      alert(`❌ Error: ${err.message}`);
                    }
                  }}
                >
                  Get Answer from LLM
                </Button>
                {config.rag_answer && (
                  <Box sx={{
                    mt: 2, p: 2, bgcolor: '#f9fbe7', borderRadius: 1,
                  }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>LLM Answer:</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {config.rag_answer}
                    </Typography>
                  </Box>
                )}
                {Array.isArray(config.rag_context_used) && config.rag_context_used.length > 0 && (
                  <Box sx={{
                    mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1,
                  }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Context Used:</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                      {config.rag_context_used.join('\n\n---\n\n')}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Configuration Summary */}
            <Box sx={{
              mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1,
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🔧 Configuration Summary:
              </Typography>
              <Typography variant="caption" display="block">
                Mode:
                {' '}
                {config.rag_mode === 'upload' ? '📤 Upload' : '🔍 Query'}
                {' '}
                |
                Collection:
                {' '}
                {config.collection || 'documents'}
              </Typography>
              {config.rag_mode === 'query' && (
                <Typography variant="caption" display="block">
                  Will retrieve top
                  {' '}
                  {config.top_k || 5}
                  {' '}
                  results with
                  {' '}
                  {config.response_format || 'context_with_query'}
                  {' '}
                  format
                </Typography>
              )}
              {config.rag_mode === 'upload' && (
                <>
                  <Typography variant="caption" display="block">
                    Will chunk
                    {' '}
                    {config.file_type || 'text'}
                    {' '}
                    files into
                    {' '}
                    {config.chunk_size || 1000}
                    {' '}
                    char pieces
                  </Typography>
                  {config.upload_status === 'completed' && (
                    <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>
                      ✅ File uploaded successfully with
                      {' '}
                      {config.chunks_added}
                      {' '}
                      chunks
                    </Typography>
                  )}
                  {config.file_path && config.upload_status !== 'completed' && (
                    <Typography variant="caption" display="block" sx={{ color: 'warning.main' }}>
                      ⚠️ File ready for upload:
                      {' '}
                      {config.file_path}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* Tips */}
            <Box sx={{
              mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1,
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                💡 Tips:
              </Typography>
              <Typography variant="caption" display="block">
                • Upload mode: Select files to upload directly to ChromaDB
              </Typography>
              <Typography variant="caption" display="block">
                • Query mode: Search previously uploaded documents
              </Typography>
              <Typography variant="caption" display="block">
                • Uploaded files are automatically chunked and indexed
              </Typography>
              <Typography variant="caption" display="block">
                • Use meaningful collection names to organize documents
              </Typography>
            </Box>

            <TextField
              label="State Variables (comma separated)"
              value={(config.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                updateConfig('stateVars', value);
              }}
              fullWidth
              placeholder="e.g. rag_context, rag_sources, rag_answer"
              helperText="State variables to store RAG results. These will be available to subsequent nodes."
              sx={{ mt: 2 }}
            />
          </Box>
        );
      case 'transform':
        return (
          <Stack spacing={3}>
            <TextField
              label="Input Schema (JSON)"
              value={JSON.stringify(config.inputSchema || {}, null, 2)}
              onChange={(e) => {
                try { handleConfigChange('inputSchema', JSON.parse(e.target.value)); } catch { // comment
                }
              }}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Output Schema (JSON)"
              value={JSON.stringify(config.outputSchema || {}, null, 2)}
              onChange={(e) => {
                try { handleConfigChange('outputSchema', JSON.parse(e.target.value)); } catch { // comment
                }
              }}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Transformation (JS)"
              value={config.transformation || ''}
              onChange={(e) => handleConfigChange('transformation', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText="JavaScript code to transform input"
            />
          </Stack>
        );
      case 'decision-tree':
      case 'switch':
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle2">Cases</Typography>
            {(config.cases || []).map((c, idx) => (
              <Box key={idx} sx={{ mb: 2, pl: 1, borderLeft: '2px solid #eee' }}>
                <TextField label="Value" value={c.value || ''} onChange={(e) => { const newCases = [...(config.cases || [])]; newCases[idx].value = e.target.value; handleConfigChange('cases', newCases); }} sx={{ mr: 1 }} />
                <TextField label="Label" value={c.label || ''} onChange={(e) => { const newCases = [...(config.cases || [])]; newCases[idx].label = e.target.value; handleConfigChange('cases', newCases); }} sx={{ mr: 1 }} />
                <TextField label="Next Node" value={c.next || ''} onChange={(e) => { const newCases = [...(config.cases || [])]; newCases[idx].next = e.target.value; handleConfigChange('cases', newCases); }} />
              </Box>
            ))}
            <Button variant="outlined" onClick={() => handleConfigChange('cases', [...(config.cases || []), { value: '', label: '', next: '' }])}>Add Case</Button>
            <TextField label="Default Next Node" value={config.defaultNext || ''} onChange={(e) => handleConfigChange('defaultNext', e.target.value)} fullWidth />
          </Stack>
        );
      case 'loop':
        return (
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Loop Type</InputLabel>
              <Select
                value={config.loopType || 'while'}
                onChange={(e) => handleConfigChange('loopType', e.target.value)}
                label="Loop Type"
              >
                <MenuItem value="while">While Loop</MenuItem>
                <MenuItem value="forEach">For Each</MenuItem>
                <MenuItem value="count">Count Loop</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Max Iterations"
              type="number"
              value={config.maxIterations || 10}
              onChange={(e) => handleConfigChange('maxIterations', parseInt(e.target.value))}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              helperText="Maximum number of iterations before breaking"
            />

            <TextField
              label="Break Condition (JavaScript)"
              value={config.breakCondition || ''}
              onChange={(e) => handleConfigChange('breakCondition', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="e.g., classification_result !== null && classification_result !== undefined && classification_result !== ''"
              helperText="JavaScript expression that evaluates to true/false. Loop breaks when true."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                  fontSize: '0.9rem',
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Aggregation Strategy</InputLabel>
              <Select
                value={config.aggregationStrategy || 'final_result'}
                onChange={(e) => handleConfigChange('aggregationStrategy', e.target.value)}
                label="Aggregation Strategy"
              >
                <MenuItem value="final_result">Final Result (last iteration)</MenuItem>
                <MenuItem value="accumulate">Accumulate (combine all results)</MenuItem>
                <MenuItem value="merge">Merge (later overrides earlier)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Loop Body Nodes</InputLabel>
              <Select
                multiple
                value={Array.isArray(config.loopBodyNodes) ? config.loopBodyNodes : []}
                onChange={(e) => handleConfigChange('loopBodyNodes', e.target.value)}
                label="Loop Body Nodes"
                renderValue={(selected) => selected.join(', ')}
              >
                {/* Show available nodes in the flow */}
                {nodes && nodes.map((node) => (
                  <MenuItem key={node.id} value={node.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {node.data?.label || node.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {node.type}
                        {' '}
                        (
                        {node.id}
                        )
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select which nodes to execute in each loop iteration. You can also manually enter node references:
                <br />
                • Node ID: "ai-agent_12345" (specific node ID)
                <br />
                • Node name: "my_ai_agent" (from node label)
                <br />
                • Node type: "ai-agent" (all nodes of this type)
              </Typography>
            </FormControl>

            <TextField
              label="Custom Node References (comma separated)"
              value={Array.isArray(config.loopBodyNodes) ? config.loopBodyNodes.join(', ') : ''}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                handleConfigChange('loopBodyNodes', value);
              }}
              fullWidth
              placeholder="e.g., my_ai_agent, ai-agent, api-call_123"
              helperText="Manually specify node references. Supports node IDs, names, or types."
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={config.debugMode !== false}
                  onChange={(e) => handleConfigChange('debugMode', e.target.checked)}
                />
              )}
              label="Enable Debug Mode"
            />

            {/* Legacy fields for backward compatibility */}
            <TextField
              label="Array/Iterable (Legacy)"
              value={config.array || ''}
              onChange={(e) => handleConfigChange('array', e.target.value)}
              fullWidth
              helperText="Legacy field for forEach loops - e.g. input.items"
            />

            <TextField
              label="Loop Type (Legacy)"
              value={config.type || ''}
              onChange={(e) => handleConfigChange('type', e.target.value)}
              fullWidth
              helperText="Legacy field - use Loop Type dropdown above instead"
            />

            {/* Loop Configuration Summary */}
            <Box sx={{
              p: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🔄 Loop Configuration Summary:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                Type:
                {' '}
                {config.loopType || 'while'}
                {' '}
                | Max Iterations:
                {' '}
                {config.maxIterations || 10}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                Strategy:
                {' '}
                {config.aggregationStrategy || 'final_result'}
                {' '}
                | Debug:
                {' '}
                {config.debugMode !== false ? 'Enabled' : 'Disabled'}
              </Typography>
              {Array.isArray(config.loopBodyNodes) && config.loopBodyNodes.length > 0 && (
                <Typography variant="caption" display="block">
                  Loop Body Nodes:
                  {' '}
                  {config.loopBodyNodes.join(', ')}
                </Typography>
              )}
              {config.breakCondition && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  Break when:
                  {' '}
                  {config.breakCondition}
                </Typography>
              )}
            </Box>

            {/* Tips */}
            <Box sx={{
              p: 2,
              bgcolor: 'warning.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.light',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                💡 Loop Node Tips:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Use
                {' '}
                <strong>while</strong>
                {' '}
                loops for conditional iteration (e.g., until classification_result is determined)
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Use
                {' '}
                <strong>forEach</strong>
                {' '}
                loops for iterating over arrays
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>Loop Body Nodes</strong>
                {' '}
                specify which nodes to execute in each iteration
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>Break Condition</strong>
                {' '}
                should be a valid JavaScript expression
              </Typography>
              <Typography variant="caption" display="block">
                • Enable
                {' '}
                <strong>Debug Mode</strong>
                {' '}
                to see detailed iteration information
              </Typography>
            </Box>
          </Stack>
        );

      case 'webhook':
      case 'http_request':
        return (
          <Stack spacing={3}>
            <TextField
              label="HTTP Method"
              select
              value={config.method || 'POST'}
              onChange={(e) => handleConfigChange('method', e.target.value)}
              fullWidth
            >
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
            </TextField>

            <TextField
              label="URL"
              value={config.url || ''}
              onChange={(e) => handleConfigChange('url', e.target.value)}
              fullWidth
              placeholder="https://api.example.com/webhook"
              helperText="Full URL with variable interpolation support: {user_id}, {api_token}, etc."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Headers (JSON)
              </Typography>
              <TextField
                multiline
                minRows={4}
                maxRows={8}
                value={JSON.stringify(config.headers || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    handleConfigChange('headers', parsed);
                  } catch (e) {
                    // Invalid JSON, keep as string for now
                  }
                }}
                fullWidth
                placeholder={'{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer {api_token}"\n}'}
                helperText="Use variable interpolation: {user_id}, {api_token}, etc."
              />
            </Box>

            <TextField
              label="Request Body"
              multiline
              minRows={3}
              maxRows={6}
              value={config.body || ''}
              onChange={(e) => handleConfigChange('body', e.target.value)}
              fullWidth
              placeholder='{"message": "Hello!", "user_id": "{user_id}"}'
              helperText="JSON or form data. Use variable interpolation: {variable_name}"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Timeout (ms)"
                type="number"
                value={config.timeout || 30000}
                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value) || 30000)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Retries"
                type="number"
                value={config.retries || 3}
                onChange={(e) => handleConfigChange('retries', parseInt(e.target.value) || 3)}
                sx={{ flex: 1 }}
              />
            </Stack>

            <TextField
              label="Response Variable"
              value={config.responseVariable || 'webhook_response'}
              onChange={(e) => handleConfigChange('responseVariable', e.target.value)}
              fullWidth
              helperText="Variable to store the API response in"
            />

            <TextField
              label="Success Status Codes"
              value={Array.isArray(config.successStatusCodes) ? config.successStatusCodes.join(', ') : '200, 201, 202'}
              onChange={(e) => {
                const codes = e.target.value.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n));
                handleConfigChange('successStatusCodes', codes);
              }}
              fullWidth
              helperText="Comma-separated status codes that indicate success"
            />

            <Box sx={{
              p: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
            }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                💡 Webhook Configuration Tips:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Use
                {' '}
                <strong>variable interpolation</strong>
                :
                {' '}
                <code>{'{user_id}'}</code>
                ,
                {' '}
                <code>{'{api_token}'}</code>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Response stored in:
                {' '}
                <code>{config.responseVariable || 'webhook_response'}</code>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Success status:
                {' '}
                <code>webhook_success</code>
                {' '}
                (boolean)
              </Typography>
              <Typography variant="caption" display="block">
                • Error details:
                {' '}
                <code>webhook_error</code>
                {' '}
                (if failed)
              </Typography>
            </Box>
          </Stack>
        );

      case 'web-crawler':
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle2">URLs to crawl (one per line or comma-separated)</Typography>
            <TextField
              label="URLs"
              multiline
              minRows={3}
              value={Array.isArray(config.urls) ? config.urls.join('\n') : (config.urls || '')}
              onChange={(e) => handleConfigChange('urls', e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))}
              fullWidth
              placeholder="https://example.com/page1&#10;https://example.com/page2"
            />
            <TextField
              label="URL Variable (optional)"
              value={config.urlVariable || ''}
              onChange={(e) => handleConfigChange('urlVariable', e.target.value)}
              fullWidth
              placeholder="e.g. urls_from_form or {{variables.urls}}"
              helperText="State variable containing URL(s) to crawl"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Depth"
                select
                value={config.depth ?? 0}
                onChange={(e) => handleConfigChange('depth', parseInt(e.target.value, 10))}
                fullWidth
                helperText="0 = single page, 1 = follow same-origin links"
              >
                <MenuItem value={0}>0 (single page)</MenuItem>
                <MenuItem value={1}>1 (follow links)</MenuItem>
              </TextField>
              <TextField
                label="Max Pages"
                type="number"
                value={config.maxPages ?? 10}
                onChange={(e) => handleConfigChange('maxPages', parseInt(e.target.value, 10))}
                fullWidth
                inputProps={{ min: 1, max: 50 }}
              />
            </Stack>
            <TextField
              label="Extract Mode"
              select
              value={config.extractMode || 'text'}
              onChange={(e) => handleConfigChange('extractMode', e.target.value)}
              fullWidth
            >
              <MenuItem value="raw_html">Raw HTML</MenuItem>
              <MenuItem value="text">Text (strip tags)</MenuItem>
              <MenuItem value="selector">CSS Selector</MenuItem>
            </TextField>
            {config.extractMode === 'selector' && (
              <TextField
                label="Selectors (JSON: name → CSS selector)"
                multiline
                minRows={2}
                value={typeof config.selectors === 'object' ? JSON.stringify(config.selectors, null, 2) : (config.selectors || '{}')}
                onChange={(e) => {
                  try {
                    handleConfigChange('selectors', JSON.parse(e.target.value));
                  } catch (err) { /* keep previous */ }
                }}
                fullWidth
                placeholder='{"title": "h1", "body": ".content"}'
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={config.respectRobotsTxt !== false}
                  onChange={(e) => handleConfigChange('respectRobotsTxt', e.target.checked)}
                />
              }
              label="Respect robots.txt"
            />
            <TextField
              label="User-Agent (optional)"
              value={config.userAgent || ''}
              onChange={(e) => handleConfigChange('userAgent', e.target.value)}
              fullWidth
              placeholder="BaiglabsCrawler/1.0 (+https://baiglabs.com/crawler-policy)"
              helperText="Leave empty for default. Set for compliance or site-specific rules."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max Concurrent"
                type="number"
                value={config.maxConcurrent ?? 2}
                onChange={(e) => handleConfigChange('maxConcurrent', parseInt(e.target.value, 10))}
                fullWidth
                inputProps={{ min: 1, max: 5 }}
                helperText="Parallel requests per run (1–5)"
              />
              <TextField
                label="Retries"
                type="number"
                value={config.retries ?? 2}
                onChange={(e) => handleConfigChange('retries', parseInt(e.target.value, 10))}
                fullWidth
                inputProps={{ min: 0, max: 5 }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={config.includeHtml === true}
                  onChange={(e) => handleConfigChange('includeHtml', e.target.checked)}
                />
              }
              label="Include raw HTML in each page (can be large)"
            />
            <TextField
              label="Allowed Domains (optional)"
              value={Array.isArray(config.allowedDomains) ? config.allowedDomains.join(', ') : (config.allowedDomains || '')}
              onChange={(e) => handleConfigChange('allowedDomains', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              fullWidth
              placeholder="example.com, sub.example.com"
              helperText="If set, only these domains will be crawled (allowlist)."
            />
            <TextField
              label="Request Delay (ms)"
              type="number"
              value={config.requestDelayMs ?? 1000}
              onChange={(e) => handleConfigChange('requestDelayMs', parseInt(e.target.value, 10))}
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Timeout (ms)"
              type="number"
              value={config.timeoutMs ?? 15000}
              onChange={(e) => handleConfigChange('timeoutMs', parseInt(e.target.value, 10))}
              fullWidth
            />
            <TextField
              label="Output Variable"
              value={config.outputVariable || 'crawled_content'}
              onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
              fullWidth
              helperText="State variable to store result (object with pages, success, errors)"
            />
          </Stack>
        );

      case 'connector':
        return (
          <Stack spacing={3}>
            <TextField
              label="Connector"
              select
              value={config.connectorId || 'http'}
              onChange={(e) => handleConfigChange('connectorId', e.target.value)}
              fullWidth
            >
              <MenuItem value="http">HTTP Request</MenuItem>
              <MenuItem value="slack">Slack</MenuItem>
              <MenuItem value="hubspot">HubSpot</MenuItem>
            </TextField>
            <TextField
              label="Operation"
              select
              value={config.operationId || 'request'}
              onChange={(e) => handleConfigChange('operationId', e.target.value)}
              fullWidth
            >
              {config.connectorId === 'slack' && <MenuItem value="send_message">Send Message</MenuItem>}
              {config.connectorId === 'hubspot' && (
                <>
                  <MenuItem value="create_contact">Create Contact</MenuItem>
                  <MenuItem value="find_contact_by_email">Find Contact by Email</MenuItem>
                </>
              )}
              {(config.connectorId === 'http' || !config.connectorId) && <MenuItem value="request">HTTP Request</MenuItem>}
            </TextField>
            <TextField
              label="Credential Reference"
              value={config.credentialRef || ''}
              onChange={(e) => handleConfigChange('credentialRef', e.target.value)}
              fullWidth
              placeholder="e.g. connector_slack_token or variable name holding credentials"
              helperText="Variable name or credential ref; store token in state or env CONNECTOR_<REF>"
            />
            <Typography variant="subtitle2">Input Mapping (param name → value or {'{{variables.name}}'})</Typography>
            <TextField
              multiline
              minRows={4}
              value={typeof config.inputMapping === 'object' ? JSON.stringify(config.inputMapping, null, 2) : (config.inputMapping || '{}')}
              onChange={(e) => {
                try {
                  handleConfigChange('inputMapping', JSON.parse(e.target.value));
                } catch (err) { /* keep */ }
              }}
              fullWidth
              placeholder='{"channel": "#general", "text": "{{variables.message}}"}'
            />
            <TextField
              label="Output Variable"
              value={config.outputVariable || 'connector_result'}
              onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
              fullWidth
              helperText="Full response is stored here (e.g. connector_result.success, connector_result.data)"
            />
            <TextField
              label="State Variables (optional)"
              value={typeof config.stateVariables === 'object' ? JSON.stringify(config.stateVariables, null, 2) : (config.stateVariables || '{}')}
              onChange={(e) => {
                try {
                  handleConfigChange('stateVariables', JSON.parse(e.target.value));
                } catch (err) { /* keep */ }
              }}
              fullWidth
              multiline
              minRows={2}
              placeholder='{"newContactId": "data.id", "success": "success"}'
              helperText="Map response paths to state variable names (e.g. data.id → newContactId)"
            />
          </Stack>
        );

      case 'execute-flow':
        return (
          <Stack spacing={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Run another flow as a subflow. The subflow receives current state variables and its result is stored in the output variable.
            </Typography>
            <ExecuteFlowIdSelect
              value={config.flowId || ''}
              onChange={(v) => handleConfigChange('flowId', v)}
            />
            <TextField
              label="Output Variable"
              value={config.outputVariable || 'subflow_result'}
              onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
              fullWidth
              helperText="State variable to store subflow result (object with success, result, variables, error)"
            />
            <FormControl fullWidth>
              <InputLabel>Input Mode</InputLabel>
              <Select
                value={config.inputMapping || 'pass_through'}
                onChange={(e) => handleConfigChange('inputMapping', e.target.value)}
                label="Input Mode"
              >
                <MenuItem value="pass_through">Pass through (all current variables)</MenuItem>
                <MenuItem value="custom">Custom mapping (configure in JSON)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );

      case 'try-catch':
        return (
          <Stack spacing={3}>
            <TextField
              label="Try Block (JS)"
              value={config.tryBlock || ''}
              onChange={(e) => handleConfigChange('tryBlock', e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Catch Block (JS)"
              value={config.catchBlock || ''}
              onChange={(e) => handleConfigChange('catchBlock', e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        );

      case 'filter':
        return (
          <Stack spacing={3}>
            <TextField
              label="Filter Condition"
              value={config.filterCondition || ''}
              onChange={(e) => handleConfigChange('filterCondition', e.target.value)}
              fullWidth
              placeholder="item.value > 10"
              helperText="JavaScript expression for filtering items"
            />
            <TextField
              label="Result Variable"
              value={config.resultVariable || 'filtered_items'}
              onChange={(e) => handleConfigChange('resultVariable', e.target.value)}
              fullWidth
              placeholder="filtered_items"
              helperText="Variable to store filtered results"
            />
          </Stack>
        );

      case 'sort':
        return (
          <Stack spacing={3}>
            <TextField
              label="Sort Key"
              value={config.sortKey || ''}
              onChange={(e) => handleConfigChange('sortKey', e.target.value)}
              fullWidth
              placeholder="item.value"
              helperText="Property to sort by"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.sortDescending || false}
                  onChange={(e) => handleConfigChange('sortDescending', e.target.checked)}
                />
              )}
              label="Sort Descending"
            />
            <TextField
              label="Result Variable"
              value={config.resultVariable || 'sorted_items'}
              onChange={(e) => handleConfigChange('resultVariable', e.target.value)}
              fullWidth
              placeholder="sorted_items"
              helperText="Variable to store sorted results"
            />
          </Stack>
        );

      case 'merge':
        return (
          <Stack spacing={3}>
            <TextField
              label="Primary Array"
              value={config.primaryArray || ''}
              onChange={(e) => handleConfigChange('primaryArray', e.target.value)}
              fullWidth
              placeholder="array1"
              helperText="Variable name of primary array"
            />
            <TextField
              label="Secondary Array"
              value={config.secondaryArray || ''}
              onChange={(e) => handleConfigChange('secondaryArray', e.target.value)}
              fullWidth
              placeholder="array2"
              helperText="Variable name of array to merge"
            />
            <TextField
              label="Merge Key"
              value={config.mergeKey || ''}
              onChange={(e) => handleConfigChange('mergeKey', e.target.value)}
              fullWidth
              placeholder="id"
              helperText="Property to merge on"
            />
            <TextField
              label="Result Variable"
              value={config.resultVariable || 'merged_array'}
              onChange={(e) => handleConfigChange('resultVariable', e.target.value)}
              fullWidth
              placeholder="merged_array"
              helperText="Variable to store merged results"
            />
          </Stack>
        );

      case 'group':
        return (
          <Stack spacing={3}>
            <TextField
              label="Group By"
              value={config.groupBy || ''}
              onChange={(e) => handleConfigChange('groupBy', e.target.value)}
              fullWidth
              placeholder="item.category"
              helperText="Property to group by"
            />
            <TextField
              label="Aggregate Function"
              select
              value={config.aggregateFunction || 'count'}
              onChange={(e) => handleConfigChange('aggregateFunction', e.target.value)}
              fullWidth
            >
              <MenuItem value="count">Count</MenuItem>
              <MenuItem value="sum">Sum</MenuItem>
              <MenuItem value="avg">Average</MenuItem>
              <MenuItem value="min">Minimum</MenuItem>
              <MenuItem value="max">Maximum</MenuItem>
            </TextField>
            <TextField
              label="Aggregate Field"
              value={config.aggregateField || ''}
              onChange={(e) => handleConfigChange('aggregateField', e.target.value)}
              fullWidth
              placeholder="item.value"
              helperText="Field to aggregate (for sum, avg, min, max)"
            />
            <TextField
              label="Result Variable"
              value={config.resultVariable || 'grouped_data'}
              onChange={(e) => handleConfigChange('resultVariable', e.target.value)}
              fullWidth
              placeholder="grouped_data"
              helperText="Variable to store grouped results"
            />
          </Stack>
        );

      case 'email':
        return (
          <Stack spacing={3}>
            <TextField
              label="To Email"
              value={config.toEmail || ''}
              onChange={(e) => handleConfigChange('toEmail', e.target.value)}
              fullWidth
              placeholder="recipient@example.com"
              helperText="Recipient email address"
            />
            <TextField
              label="Subject"
              value={config.subject || ''}
              onChange={(e) => handleConfigChange('subject', e.target.value)}
              fullWidth
              placeholder="Email subject"
              helperText="Email subject line"
            />
            <TextField
              label="Message"
              multiline
              minRows={3}
              maxRows={6}
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              fullWidth
              placeholder="Email message content"
              helperText="Email message body"
            />
          </Stack>
        );

      case 'sms':
        return (
          <Stack spacing={3}>
            <TextField
              label="To Phone Number"
              value={config.toPhone || ''}
              onChange={(e) => handleConfigChange('toPhone', e.target.value)}
              fullWidth
              placeholder="+1234567890"
              helperText="Recipient phone number"
            />
            <TextField
              label="Message"
              multiline
              minRows={2}
              maxRows={4}
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              fullWidth
              placeholder="SMS message content"
              helperText="SMS message body"
            />
          </Stack>
        );

      case 'chat':
        return (
          <Stack spacing={3}>
            <TextField
              label="Chat Message"
              multiline
              minRows={3}
              maxRows={6}
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              fullWidth
              placeholder="Chat message content"
              helperText="Message to send in chat"
            />
          </Stack>
        );

      case 'message-queue':
        return (
          <Stack spacing={3}>
            <TextField
              label="Queue Name"
              value={config.queueName || ''}
              onChange={(e) => handleConfigChange('queueName', e.target.value)}
              fullWidth
              placeholder="my-queue"
              helperText="Name of the message queue"
            />
            <TextField
              label="Message"
              multiline
              minRows={2}
              maxRows={4}
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              fullWidth
              placeholder="Message content"
              helperText="Message to send to queue"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.isPublish !== false}
                  onChange={(e) => handleConfigChange('isPublish', e.target.checked)}
                />
              )}
              label="Publish Message"
              helperText="Send message to queue (true) or receive from queue (false)"
            />
          </Stack>
        );

      case 'voice-call':
        return (
          <Stack spacing={3}>
            <TextField
              label="To Phone Number"
              value={config.toPhone || ''}
              onChange={(e) => handleConfigChange('toPhone', e.target.value)}
              fullWidth
              placeholder="+1234567890"
              helperText="Recipient phone number"
            />
            <TextField
              label="Voice Message"
              multiline
              minRows={2}
              maxRows={4}
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              fullWidth
              placeholder="Voice call message content"
              helperText="Message to convert to speech"
            />
          </Stack>
        );

      case 'social-post':
        return (
          <Stack spacing={3}>
            <TextField
              label="Platform"
              select
              value={config.platform || 'twitter'}
              onChange={(e) => handleConfigChange('platform', e.target.value)}
              fullWidth
            >
              <MenuItem value="twitter">Twitter</MenuItem>
              <MenuItem value="facebook">Facebook</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="linkedin">LinkedIn</MenuItem>
            </TextField>
            <TextField
              label="Post Content"
              multiline
              minRows={3}
              maxRows={6}
              value={config.content || ''}
              onChange={(e) => handleConfigChange('content', e.target.value)}
              fullWidth
              placeholder="Social media post content"
              helperText="Content to post on social media"
            />
          </Stack>
        );

      case 'delay':
        return (
          <Stack spacing={3}>
            <TextField
              label="Delay Duration"
              type="number"
              value={config.delaySeconds || 5}
              onChange={(e) => handleConfigChange('delaySeconds', parseInt(e.target.value) || 5)}
              fullWidth
              helperText="Delay in seconds"
            />
            <TextField
              label="Delay Unit"
              select
              value={config.delayUnit || 'seconds'}
              onChange={(e) => handleConfigChange('delayUnit', e.target.value)}
              fullWidth
            >
              <MenuItem value="seconds">Seconds</MenuItem>
              <MenuItem value="minutes">Minutes</MenuItem>
              <MenuItem value="hours">Hours</MenuItem>
            </TextField>
          </Stack>
        );

      case 'trigger':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              🎯 Trigger Configuration
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Trigger Type</InputLabel>
              <Select
                value={config.triggerType || 'webhook'}
                onChange={(e) => handleConfigChange('triggerType', e.target.value)}
                label="Trigger Type"
              >
                <MenuItem value="webhook">Webhook (HTTP Request)</MenuItem>
                <MenuItem value="schedule">Schedule (Cron Job)</MenuItem>
                <MenuItem value="database_insert">Database Insert</MenuItem>
                <MenuItem value="database_update">Database Update</MenuItem>
                <MenuItem value="status_change">Status Change</MenuItem>
                <MenuItem value="manual">Manual Trigger</MenuItem>
              </Select>
            </FormControl>

            {config.triggerType === 'schedule' && (
              <TextField
                label="Schedule (Cron Format)"
                value={config.schedule || '0 8 * * *'}
                onChange={(e) => handleConfigChange('schedule', e.target.value)}
                fullWidth
                helperText="Cron format: 0 8 * * * = Every day at 8 AM"
                placeholder="0 8 * * *"
              />
            )}

            {config.triggerType === 'webhook' && (
              <>
                <TextField
                  label="Webhook Path"
                  value={config.webhookPath || '/trigger/workflow'}
                  onChange={(e) => handleConfigChange('webhookPath', e.target.value)}
                  fullWidth
                  helperText="URL path for webhook endpoint"
                  placeholder="/trigger/workflow"
                />
                <FormControl fullWidth>
                  <InputLabel>HTTP Method</InputLabel>
                  <Select
                    value={config.method || 'POST'}
                    onChange={(e) => handleConfigChange('method', e.target.value)}
                    label="HTTP Method"
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {(config.triggerType === 'database_insert' || config.triggerType === 'database_update') && (
              <>
                <TextField
                  label="Collection Name"
                  value={config.collection || ''}
                  onChange={(e) => handleConfigChange('collection', e.target.value)}
                  fullWidth
                  helperText="MongoDB collection to monitor"
                  placeholder="appointments"
                />
                <TextField
                  label="Filter Criteria (JSON)"
                  multiline
                  rows={3}
                  value={config.filter || '{}'}
                  onChange={(e) => handleConfigChange('filter', e.target.value)}
                  fullWidth
                  helperText="Optional: Filter which documents trigger the workflow"
                  placeholder='{"status": "completed"}'
                />
              </>
            )}

            {config.triggerType === 'status_change' && (
              <>
                <TextField
                  label="Status Field"
                  value={config.statusField || 'status'}
                  onChange={(e) => handleConfigChange('statusField', e.target.value)}
                  fullWidth
                  helperText="Field name to monitor for status changes"
                  placeholder="status"
                />
                <TextField
                  label="Target Status"
                  value={config.targetStatus || ''}
                  onChange={(e) => handleConfigChange('targetStatus', e.target.value)}
                  fullWidth
                  helperText="Status value that triggers the workflow"
                  placeholder="completed"
                />
              </>
            )}

            <TextField
              label="Description"
              multiline
              rows={2}
              value={config.description || ''}
              onChange={(e) => handleConfigChange('description', e.target.value)}
              fullWidth
              helperText="What triggers this workflow?"
              placeholder="Triggered when appointment is completed"
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Trigger Node:</strong>
                {' '}
                This node starts the workflow based on external events.
                Choose the trigger type that matches your use case.
              </Typography>
            </Alert>
          </Stack>
        );

      case 'tool':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              🔧 Tool Configuration
            </Typography>

            <TextField
              label="Tool Name"
              value={config.tool_name || ''}
              onChange={(e) => handleConfigChange('tool_name', e.target.value)}
              fullWidth
              helperText="Name of the tool to execute (e.g., calculator, search, file_reader)"
            />

            <TextField
              label="Tool Parameters (JSON)"
              multiline
              rows={4}
              value={JSON.stringify(config.tool_params || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange('tool_params', parsed);
                } catch (err) {
                  console.warn('Invalid JSON in tool parameters:', err);
                }
              }}
              fullWidth
              placeholder='{\n  "expression": "2 + 2 * 3"\n}'
              sx={{
                fontFamily: 'monospace',
                '& .MuiInputBase-root': {
                  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                },
              }}
              helperText="Parameters to pass to the tool"
            />

            <TextField
              label="Timeout (ms)"
              type="number"
              value={config.timeout || 30000}
              onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
              fullWidth
            />

            <TextField
              label="Retries"
              type="number"
              value={config.retries || 3}
              onChange={(e) => handleConfigChange('retries', parseInt(e.target.value))}
              fullWidth
            />

            <Typography variant="body2" color="text.secondary">
              💡 Available tools: calculator, search, file_reader, database_query
            </Typography>
          </Stack>
        );
      case 'memory':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              🧠 Memory Configuration
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Operation</InputLabel>
              <Select
                value={config.operation || 'store'}
                onChange={(e) => handleConfigChange('operation', e.target.value)}
                label="Operation"
              >
                <MenuItem value="store">Store Data</MenuItem>
                <MenuItem value="retrieve">Retrieve Data</MenuItem>
                <MenuItem value="clear">Clear Data</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Memory Key"
              value={config.key || 'default'}
              onChange={(e) => handleConfigChange('key', e.target.value)}
              fullWidth
              helperText="Key for storing/retrieving data"
            />

            {config.operation === 'store' && (
              <TextField
                label="Data to Store (JSON)"
                multiline
                rows={4}
                value={JSON.stringify(config.data || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    handleConfigChange('data', parsed);
                  } catch (err) {
                    console.warn('Invalid JSON in data:', err);
                  }
                }}
                fullWidth
                placeholder='{\n  "user_preferences": {\n    "theme": "dark",\n    "language": "en"\n  }\n}'
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                  },
                }}
                helperText="Data to store in memory"
              />
            )}

            <Typography variant="body2" color="text.secondary">
              💡 Memory operations allow you to store, retrieve, and clear data during flow execution.
            </Typography>
          </Stack>
        );
      case 'aggregate':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              📊 Aggregate Configuration
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Aggregate Type</InputLabel>
              <Select
                value={config.aggregate_type || 'sum'}
                onChange={(e) => handleConfigChange('aggregate_type', e.target.value)}
                label="Aggregate Type"
              >
                <MenuItem value="sum">Sum</MenuItem>
                <MenuItem value="count">Count</MenuItem>
                <MenuItem value="average">Average</MenuItem>
                <MenuItem value="max">Maximum</MenuItem>
                <MenuItem value="min">Minimum</MenuItem>
                <MenuItem value="concat">Concatenate</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Field Name"
              value={config.field || 'value'}
              onChange={(e) => handleConfigChange('field', e.target.value)}
              fullWidth
              helperText="Field name to aggregate (for objects) or leave empty for direct values"
            />

            <TextField
              label="Input Data (JSON array)"
              multiline
              rows={4}
              value={JSON.stringify(config.input_data || [], null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (Array.isArray(parsed)) {
                    handleConfigChange('input_data', parsed);
                  }
                } catch (err) {
                  console.warn('Invalid JSON in input data:', err);
                }
              }}
              fullWidth
              placeholder='[\n  {"value": 10},\n  {"value": 20},\n  {"value": 30}\n]'
              sx={{
                fontFamily: 'monospace',
                '& .MuiInputBase-root': {
                  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                },
              }}
              helperText="Array of data to aggregate (optional - will use flow input if empty)"
            />

            <Typography variant="body2" color="text.secondary">
              💡 Aggregate operations: sum, count, average, max, min, concat
            </Typography>
          </Stack>
        );

      case 'memory-store':
        return (
          <Stack spacing={3}>
            <TextField
              label="Memory Type"
              value={config.memoryType || 'conversation'}
              onChange={(e) => handleConfigChange('memoryType', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="conversation">Conversation History</MenuItem>
              <MenuItem value="semantic">Semantic Memory</MenuItem>
              <MenuItem value="episodic">Episodic Memory</MenuItem>
              <MenuItem value="working">Working Memory</MenuItem>
            </TextField>
            <TextField
              label="Key"
              value={config.key || ''}
              onChange={(e) => handleConfigChange('key', e.target.value)}
              fullWidth
              helperText="Memory key for storage/retrieval"
            />
            <TextField
              label="Max Entries"
              value={config.maxEntries || '100'}
              onChange={(e) => handleConfigChange('maxEntries', e.target.value)}
              fullWidth
              type="number"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.persistent || false}
                  onChange={(e) => handleConfigChange('persistent', e.target.checked)}
                />
              )}
              label="Persistent Storage"
            />
          </Stack>
        );
      case 'human-input':
        return (
          <Stack spacing={3}>
            <TextField
              label="Input Type"
              value={config.inputType || 'text'}
              onChange={(e) => handleConfigChange('inputType', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="text">Text Input</MenuItem>
              <MenuItem value="file">File Upload</MenuItem>
              <MenuItem value="choice">Multiple Choice</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="approval">Approval/Rejection</MenuItem>
            </TextField>
            <TextField
              label="Prompt"
              value={config.prompt || ''}
              onChange={(e) => handleConfigChange('prompt', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Question or instruction for the user"
            />
            <TextField
              label="Timeout (seconds)"
              value={config.timeout || '300'}
              onChange={(e) => handleConfigChange('timeout', e.target.value)}
              fullWidth
              type="number"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.required || true}
                  onChange={(e) => handleConfigChange('required', e.target.checked)}
                />
              )}
              label="Required Input"
            />
          </Stack>
        );
      case 'file-operations':
        return (
          <Stack spacing={3}>
            <TextField
              label="Operation Type"
              value={config.operationType || 'read'}
              onChange={(e) => handleConfigChange('operationType', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="read">Read File</MenuItem>
              <MenuItem value="write">Write File</MenuItem>
              <MenuItem value="append">Append to File</MenuItem>
              <MenuItem value="delete">Delete File</MenuItem>
              <MenuItem value="copy">Copy File</MenuItem>
              <MenuItem value="move">Move File</MenuItem>
            </TextField>
            <TextField
              label="File Path"
              value={config.filePath || ''}
              onChange={(e) => handleConfigChange('filePath', e.target.value)}
              fullWidth
              helperText="Path to the file"
            />
            <TextField
              label="File Format"
              value={config.fileFormat || 'text'}
              onChange={(e) => handleConfigChange('fileFormat', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="xml">XML</MenuItem>
              <MenuItem value="binary">Binary</MenuItem>
            </TextField>
            <FormControlLabel
              control={(
                <Switch
                  checked={config.createIfNotExists || false}
                  onChange={(e) => handleConfigChange('createIfNotExists', e.target.checked)}
                />
              )}
              label="Create if not exists"
            />
          </Stack>
        );
      case 'data-validation':
        return (
          <Stack spacing={3}>
            <TextField
              label="Validation Type"
              value={config.validationType || 'schema'}
              onChange={(e) => handleConfigChange('validationType', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="schema">JSON Schema</MenuItem>
              <MenuItem value="regex">Regular Expression</MenuItem>
              <MenuItem value="custom">Custom Function</MenuItem>
              <MenuItem value="type">Type Check</MenuItem>
              <MenuItem value="range">Range Check</MenuItem>
            </TextField>
            <TextField
              label="Validation Rules"
              value={config.validationRules || ''}
              onChange={(e) => handleConfigChange('validationRules', e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="JSON schema, regex pattern, or validation logic"
            />
            <TextField
              label="Error Message"
              value={config.errorMessage || 'Validation failed'}
              onChange={(e) => handleConfigChange('errorMessage', e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.strictMode || false}
                  onChange={(e) => handleConfigChange('strictMode', e.target.checked)}
                />
              )}
              label="Strict Mode (fail on first error)"
            />
          </Stack>
        );
      case 'calendar':
        return (
          <Stack spacing={3}>
            <TextField
              label="Calendar Type"
              value={config.calendarType || 'google'}
              onChange={(e) => handleConfigChange('calendarType', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="google">Google Calendar</MenuItem>
              <MenuItem value="outlook">Outlook Calendar</MenuItem>
              <MenuItem value="ical">iCal</MenuItem>
              <MenuItem value="custom">Custom Calendar</MenuItem>
            </TextField>
            <TextField
              label="Operation"
              value={config.operation || 'create'}
              onChange={(e) => handleConfigChange('operation', e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="create">Create Event</MenuItem>
              <MenuItem value="read">Read Events</MenuItem>
              <MenuItem value="update">Update Event</MenuItem>
              <MenuItem value="delete">Delete Event</MenuItem>
              <MenuItem value="list">List Events</MenuItem>
              <MenuItem value="check">Check Availability</MenuItem>
            </TextField>
            <TextField
              label="Event Title"
              value={config.eventTitle || ''}
              onChange={(e) => handleConfigChange('eventTitle', e.target.value)}
              fullWidth
              helperText="Title for the calendar event"
            />
            <TextField
              label="Start Date/Time"
              value={config.startDateTime || ''}
              onChange={(e) => handleConfigChange('startDateTime', e.target.value)}
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date/Time"
              value={config.endDateTime || ''}
              onChange={(e) => handleConfigChange('endDateTime', e.target.value)}
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description"
              value={config.description || ''}
              onChange={(e) => handleConfigChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Attendees (comma-separated emails)"
              value={config.attendees || ''}
              onChange={(e) => handleConfigChange('attendees', e.target.value)}
              fullWidth
              helperText="email1@example.com, email2@example.com"
            />
            <TextField
              label="Location"
              value={config.location || ''}
              onChange={(e) => handleConfigChange('location', e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.sendInvites || true}
                  onChange={(e) => handleConfigChange('sendInvites', e.target.checked)}
                />
              )}
              label="Send Invitations"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.reminder || false}
                  onChange={(e) => handleConfigChange('reminder', e.target.checked)}
                />
              )}
              label="Set Reminder"
            />
          </Stack>
        );
      case 'form':
        return (
          <Stack spacing={3}>
            {/* Form Builder Integration */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Form Source
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                value={config.formSource || 'manual'}
                onChange={(e) => handleConfigChange('formSource', e.target.value)}
                row
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Manual Fields"
                />
                <FormControlLabel
                  value="formBuilder"
                  control={<Radio />}
                  label="Form Builder Form"
                />
              </RadioGroup>
            </FormControl>

            {config.formSource === 'formBuilder' ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Select Form Builder Form
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Form</InputLabel>
                  <Select
                    value={config.selectedFormId || ''}
                    label="Select Form"
                    onChange={(e) => handleConfigChange('selectedFormId', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select a form...</em>
                    </MenuItem>
                    {formBuilderForms.map((form) => (
                      <MenuItem key={form.id} value={form.id}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {form.name}
                          </Typography>
                          {form.description && (
                            <Typography variant="caption" color="text.secondary">
                              {form.description}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {config.selectedFormId && (
                  <Typography variant="body2" color="text.secondary">
                    Selected form will automatically load its fields and configuration from FormBuilder.
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Manual Form Fields
                </Typography>
                {(config.fields || []).map((field, idx) => (
                  field && (
                    <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <TextField
                        label="Name"
                        value={field?.name || ''}
                        onChange={(e) => {
                          const fields = [...(config.fields || [])];
                          fields[idx].name = e.target.value;
                          handleConfigChange('fields', fields);
                        }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Label"
                        value={field?.label || ''}
                        onChange={(e) => {
                          const fields = [...(config.fields || [])];
                          fields[idx].label = e.target.value;
                          handleConfigChange('fields', fields);
                        }}
                        sx={{ flex: 1 }}
                      />
                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={field?.type || 'text'}
                          label="Type"
                          onChange={(e) => {
                            const fields = [...(config.fields || [])];
                            fields[idx].type = e.target.value;
                            handleConfigChange('fields', fields);
                          }}
                        >
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="number">Number</MenuItem>
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="date">Date</MenuItem>
                          <MenuItem value="textarea">Textarea</MenuItem>
                          <MenuItem value="select">Select</MenuItem>
                          <MenuItem value="checkbox">Checkbox</MenuItem>
                          <MenuItem value="address">Address</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Default"
                        value={field?.default || ''}
                        onChange={(e) => {
                          const fields = [...(config.fields || [])];
                          fields[idx].default = e.target.value;
                          handleConfigChange('fields', fields);
                        }}
                        sx={{ flex: 1 }}
                      />
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={!!field?.required}
                            onChange={(e) => {
                              const fields = [...(config.fields || [])];
                              fields[idx].required = e.target.checked;
                              handleConfigChange('fields', fields);
                            }}
                          />
                        )}
                        label="Required"
                        sx={{ ml: 1 }}
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const fields = [...(config.fields || [])];
                          fields.splice(idx, 1);
                          handleConfigChange('fields', fields);
                        }}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        ✕
                      </Button>
                    </Box>
                  )
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleConfigChange('fields', [...(config.fields || []), {
                    name: '', label: '', type: 'text', required: false, default: '',
                  }])}
                >
                  Add Field
                </Button>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Form Submission Settings */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Form Submission
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Submit To</InputLabel>
              <Select
                value={config.submitTo || 'state'}
                label="Submit To"
                onChange={(e) => handleConfigChange('submitTo', e.target.value)}
              >
                <MenuItem value="state">State Variable</MenuItem>
                <MenuItem value="database">Database Table</MenuItem>
                <MenuItem value="api">API Endpoint</MenuItem>
                <MenuItem value="none">None (just collect)</MenuItem>
              </Select>
            </FormControl>

            {config.submitTo === 'state' && (
              <TextField
                label="State Variable Name"
                value={config.stateVariable || ''}
                onChange={(e) => handleConfigChange('stateVariable', e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="e.g., userInfo, preferences"
                helperText="Which state variable should this form update?"
              />
            )}
            {config.submitTo === 'database' && (
              <TextField
                label="Table Name"
                value={config.table || ''}
                onChange={(e) => handleConfigChange('table', e.target.value)}
                fullWidth
                helperText="Database table to insert form data"
              />
            )}
            {config.submitTo === 'api' && (
              <TextField
                label="API Endpoint"
                value={config.apiEndpoint || ''}
                onChange={(e) => handleConfigChange('apiEndpoint', e.target.value)}
                fullWidth
                helperText="API endpoint to POST form data"
              />
            )}
            <TextField
              label="Submit Button Label"
              value={config.submitLabel || 'Submit'}
              onChange={(e) => handleConfigChange('submitLabel', e.target.value)}
              fullWidth
            />
            <TextField
              label="Success Message"
              value={config.successMessage || ''}
              onChange={(e) => handleConfigChange('successMessage', e.target.value)}
              fullWidth
            />
            <TextField
              label="Error Message"
              value={config.errorMessage || ''}
              onChange={(e) => handleConfigChange('errorMessage', e.target.value)}
              fullWidth
            />

            <Divider sx={{ my: 2 }} />

            {/* Dynamic Form Generation Settings */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Dynamic Form Generation
            </Typography>

            <FormControlLabel
              control={(
                <Switch
                  checked={config.dynamicGeneration || false}
                  onChange={(e) => handleConfigChange('dynamicGeneration', e.target.checked)}
                />
              )}
              label="Enable Dynamic Generation"
              sx={{ mb: 1 }}
            />

            {config.dynamicGeneration && (
              <>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={config.aiGenerated || false}
                      onChange={(e) => handleConfigChange('aiGenerated', e.target.checked)}
                    />
                  )}
                  label="AI-Generated Fields"
                  sx={{ mb: 1 }}
                />

                <FormControlLabel
                  control={(
                    <Switch
                      checked={config.contextBased || false}
                      onChange={(e) => handleConfigChange('contextBased', e.target.checked)}
                    />
                  )}
                  label="Context-Based Fields"
                  sx={{ mb: 1 }}
                />

                <TextField
                  label="Dynamic Source Variable"
                  value={config.dynamicSource || ''}
                  onChange={(e) => handleConfigChange('dynamicSource', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="e.g., form_fields_json, dynamic_fields"
                  helperText="State variable containing JSON array of form fields"
                />

                <TextField
                  label="Context Prompt"
                  value={config.contextPrompt || ''}
                  onChange={(e) => handleConfigChange('contextPrompt', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                  placeholder="Describe what kind of form fields to generate based on user input..."
                  helperText="Optional: Custom prompt for AI field generation"
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* State Update Settings */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              State Update Settings
            </Typography>

            <FormControlLabel
              control={(
                <Switch
                  checked={config.updateState !== false}
                  onChange={(e) => handleConfigChange('updateState', e.target.checked)}
                />
              )}
              label="Update State Variables"
              sx={{ mb: 1 }}
            />

            {config.updateState && (
              <TextField
                label="State Field Mapping (JSON)"
                value={JSON.stringify(config.stateMapping || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    if (parsed && typeof parsed === 'object') {
                      handleConfigChange('stateMapping', parsed);
                    }
                  } catch (err) {
                    // Invalid JSON, ignore for now
                    console.warn('Invalid JSON in state mapping:', err);
                  }
                }}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
                placeholder='{"name": "userInfo.name", "email": "userInfo.email"}'
                helperText="Map form fields to state variable properties"
              />
            )}

            <TextField
              label="Form Output Variables (comma separated)"
              value={(config.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                handleConfigChange('stateVars', value);
              }}
              fullWidth
              placeholder="e.g. form_data, user_input, address"
              helperText="State variables where form submission data will be stored. These are the variables that will contain the user's form input after submission."
            />
          </Stack>
        );

      case 'database':
      case 'db_query':
      case 'db_insert':
      case 'db_update':
      case 'db_delete':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              🗄️ Database Operation Configuration
            </Typography>

            <TextField
              label="Connection String"
              value={config.connectionString || 'mongodb://localhost:27017'}
              onChange={(e) => handleConfigChange('connectionString', e.target.value)}
              fullWidth
              placeholder="mongodb://localhost:27017"
              helperText="MongoDB connection string"
            />

            <TextField
              label="Database Name"
              value={config.database || 'flowdb'}
              onChange={(e) => handleConfigChange('database', e.target.value)}
              fullWidth
              placeholder="flowdb"
              helperText="Database name"
            />

            <TextField
              label="Collection/Table"
              value={config.collection || 'documents'}
              onChange={(e) => handleConfigChange('collection', e.target.value)}
              fullWidth
              placeholder="documents"
              helperText="Collection or table name"
            />

            <TextField
              label="Operation"
              select
              value={config.operation || 'find'}
              onChange={(e) => handleConfigChange('operation', e.target.value)}
              fullWidth
            >
              <MenuItem value="find">Find (Query)</MenuItem>
              <MenuItem value="find_one">Find One</MenuItem>
              <MenuItem value="insert_one">Insert One</MenuItem>
              <MenuItem value="insert_many">Insert Many</MenuItem>
              <MenuItem value="update_one">Update One</MenuItem>
              <MenuItem value="update_many">Update Many</MenuItem>
              <MenuItem value="delete_one">Delete One</MenuItem>
              <MenuItem value="delete_many">Delete Many</MenuItem>
              <MenuItem value="count">Count</MenuItem>
              <MenuItem value="aggregate">Aggregate</MenuItem>
            </TextField>

            <TextField
              label="Query/Filter (JSON)"
              multiline
              minRows={3}
              maxRows={6}
              value={JSON.stringify(config.query || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange('query', parsed);
                } catch (e) {
                  // Invalid JSON, keep as string for now
                }
              }}
              fullWidth
              placeholder={'{\n  "user_id": "{user_id}",\n  "status": "active"\n}'}
              helperText="MongoDB query/filter. Use variable interpolation: {user_id}"
            />

            <TextField
              label="Projection (JSON)"
              multiline
              minRows={2}
              maxRows={4}
              value={JSON.stringify(config.projection || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange('projection', parsed);
                } catch (e) {
                  // Invalid JSON, keep as string for now
                }
              }}
              fullWidth
              placeholder={'{\n  "_id": 0,\n  "name": 1,\n  "email": 1\n}'}
              helperText="Fields to include/exclude. Use variable interpolation."
            />

            <TextField
              label="Document/Update (JSON)"
              multiline
              minRows={3}
              maxRows={6}
              value={JSON.stringify(config.document || config.update || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (config.operation?.includes('update')) {
                    handleConfigChange('update', parsed);
                  } else {
                    handleConfigChange('document', parsed);
                  }
                } catch (e) {
                  // Invalid JSON, keep as string for now
                }
              }}
              fullWidth
              placeholder={'{\n  "name": "{user_name}",\n  "email": "{user_email}",\n  "updated_at": "{timestamp}"\n}'}
              helperText="Document for insert, or update operations. Use variable interpolation."
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Limit"
                type="number"
                value={config.limit || 10}
                onChange={(e) => handleConfigChange('limit', parseInt(e.target.value) || 10)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Skip"
                type="number"
                value={config.skip || 0}
                onChange={(e) => handleConfigChange('skip', parseInt(e.target.value) || 0)}
                sx={{ flex: 1 }}
              />
            </Stack>

            <TextField
              label="Sort (JSON)"
              value={JSON.stringify(config.sort || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleConfigChange('sort', parsed);
                } catch (e) {
                  // Invalid JSON, keep as string for now
                }
              }}
              fullWidth
              placeholder={'{\n  "created_at": -1,\n  "name": 1\n}'}
              helperText="Sort specification: 1 for ascending, -1 for descending"
            />

            <TextField
              label="Result Variable"
              value={config.resultVariable || 'db_result'}
              onChange={(e) => handleConfigChange('resultVariable', e.target.value)}
              fullWidth
              helperText="Variable to store the database result in"
            />

            <TextField
              label="Error Variable"
              value={config.errorVariable || 'db_error'}
              onChange={(e) => handleConfigChange('errorVariable', e.target.value)}
              fullWidth
              helperText="Variable to store error details in"
            />

            <Box sx={{
              p: 2,
              bgcolor: 'success.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.light',
            }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                💡 Database Configuration Tips:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Use
                {' '}
                <strong>variable interpolation</strong>
                :
                {' '}
                <code>{'{user_id}'}</code>
                ,
                {' '}
                <code>{'{timestamp}'}</code>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Result stored in:
                {' '}
                <code>{config.resultVariable || 'db_result'}</code>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Success status:
                {' '}
                <code>db_success</code>
                {' '}
                (boolean)
              </Typography>
              <Typography variant="caption" display="block">
                • Error details:
                {' '}
                <code>{config.errorVariable || 'db_error'}</code>
                {' '}
                (if failed)
              </Typography>
            </Box>
          </Stack>
        );

      case 'mcp-tool':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              🔧 MCP Tool Configuration
            </Typography>

            {/* MCP Server Connection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                MCP Server Connection
              </Typography>
              <TextField
                label="Server URL"
                value={config.serverUrl || getLangGraphWsUrl('/api/v1/mcp/ws')}
                onChange={(e) => handleConfigChange('serverUrl', e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="ws://localhost:8765/ws"
                helperText="WebSocket URL for MCP server (production backend)"
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Authentication</InputLabel>
                <Select
                  value={config.authentication || 'none'}
                  onChange={(e) => handleConfigChange('authentication', e.target.value)}
                  label="Authentication"
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="token">Token</MenuItem>
                  <MenuItem value="basic">Basic Auth</MenuItem>
                </Select>
              </FormControl>

              {config.authentication === 'token' && (
                <TextField
                  label="Auth Token"
                  value={config.authToken || ''}
                  onChange={(e) => handleConfigChange('authToken', e.target.value)}
                  fullWidth
                  type="password"
                  sx={{ mb: 2 }}
                  placeholder="Bearer token or API key"
                />
              )}
            </Box>

            {/* Tool Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Tool Configuration
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Available MCP Tools</InputLabel>
                <Select
                  value={config.toolName || ''}
                  onChange={(e) => handleConfigChange('toolName', e.target.value)}
                  label="Available MCP Tools"
                >
                  <MenuItem value="">
                    <em>Select a tool...</em>
                  </MenuItem>
                  <MenuItem value="database_query">
                    🔍 Database Query - Execute SQL queries with context
                  </MenuItem>
                  <MenuItem value="file_operations">
                    📁 File Operations - Read, write, and list files
                  </MenuItem>
                  <MenuItem value="ai_processor">
                    🤖 AI Processor - Process data with AI models
                  </MenuItem>
                  <MenuItem value="http_request">
                    🌐 HTTP Request - Make HTTP requests to external APIs
                  </MenuItem>
                </Select>
                <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  💡
                  {' '}
                  {config.toolName ? `Selected: ${config.toolName}` : 'Choose a tool to see its configuration options'}
                </Typography>
              </FormControl>

              {/* Tool Arguments with Templates */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tool Arguments (JSON)
                </Typography>

                {/* Tool-specific examples */}
                {config.toolName && (
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      💡
                      {' '}
                      {config.toolName}
                      {' '}
                      Configuration Example:
                    </Typography>
                    {config.toolName === 'database_query' && (
                      <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {`{
  "query": "SELECT * FROM customers WHERE email = ?",
  "parameters": ["john.doe@example.com"],
  "context_vars": {
    "user_id": "context.getVar('current_user_id')",
    "status": "context.getVar('customer_status')"
  }
}`}
                      </pre>
                    )}
                    {config.toolName === 'file_operations' && (
                      <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {`{
  "operation": "read",
  "path": "/data/customer_data.csv",
  "format": "csv"
}`}
                      </pre>
                    )}
                    {config.toolName === 'ai_processor' && (
                      <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {`{
  "task": "summarize",
  "data": "context.getVar('customer_query')",
  "model": "${DEFAULT_OLLAMA_MODEL}",
  "max_tokens": 500
}`}
                      </pre>
                    )}
                    {config.toolName === 'http_request' && (
                      <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {`{
  "url": "https://api.example.com/customers",
  "method": "GET",
  "headers": {
    "Authorization": "Bearer context.getVar('api_token')"
  }
}`}
                      </pre>
                    )}
                  </Alert>
                )}

                <TextField
                  multiline
                  minRows={config.toolName ? 6 : 4}
                  maxRows={12}
                  value={config.toolName
                    ? JSON.stringify(config.toolArguments || getToolDefaults(config.toolName), null, 2)
                    : '{\n  "key": "value",\n  "array": [],\n  "object": {}\n}'}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      if (parsed && typeof parsed === 'object') {
                        handleConfigChange('toolArguments', parsed);
                      }
                    } catch (err) {
                      console.warn('Invalid JSON in tool arguments:', err);
                    }
                  }}
                  fullWidth
                  placeholder='{\n  "path": "/path/to/file",\n  "query": "search term"\n}'
                  sx={{
                    fontFamily: 'monospace',
                    '& .MuiInputBase-root': {
                      fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.4,
                    },
                  }}
                  helperText="Arguments to pass to the MCP tool. Use context.getVar('variable_name') to access flow context."
                />
              </Box>
            </Box>

            {/* Execution Settings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Execution Settings
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Timeout (ms)"
                  type="number"
                  value={config.timeout || 30000}
                  onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
                  inputProps={{ min: 1000, max: 300000, step: 1000 }}
                  helperText="Execution timeout"
                />
                <TextField
                  label="Retries"
                  type="number"
                  value={config.retries || 3}
                  onChange={(e) => handleConfigChange('retries', parseInt(e.target.value))}
                  inputProps={{ min: 0, max: 10 }}
                  helperText="Number of retry attempts"
                />
              </Box>
            </Box>

            {/* Quick Tool Examples */}
            <Box sx={{
              p: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🚀 Common MCP Tool Examples:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>database_query</strong>
                {' '}
                - Execute SQL queries with context
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>file_operations</strong>
                {' '}
                - Read, write, and list files
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>ai_processor</strong>
                {' '}
                - Process data with AI models
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>http_request</strong>
                {' '}
                - Make HTTP requests to external APIs
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>Context Integration</strong>
                {' '}
                - Automatically access flow variables
              </Typography>
              <Typography variant="caption" display="block">
                •
                {' '}
                <strong>Real-time Monitoring</strong>
                {' '}
                - Track performance and errors
              </Typography>
            </Box>

            {/* Test Connection Button */}
            <Button
              variant="outlined"
              color="primary"
              onClick={async () => {
                try {
                  // Test MCP server connection
                  const response = await fetch('/api/v1/mcp/test-connection', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      serverUrl: config.serverUrl,
                      authentication: config.authentication,
                      authToken: config.authToken,
                    }),
                  });
                  const result = await response.json();
                  if (result.success) {
                    alert('✅ MCP server connection successful!');
                  } else {
                    alert(`❌ Connection failed: ${result.error}`);
                  }
                } catch (err) {
                  alert(`❌ Connection error: ${err.message}`);
                }
              }}
              sx={{ mt: 2 }}
            >
              Test MCP Connection
            </Button>
          </Stack>
        );

      case 'logger':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              📝 Logger Configuration
            </Typography>

            {/* Log Level */}
            <FormControl fullWidth>
              <InputLabel>Log Level</InputLabel>
              <Select
                label="Log Level"
                value={config.log_level || 'INFO'}
                onChange={(e) => handleConfigChange('log_level', e.target.value)}
              >
                <MenuItem value="DEBUG">🔍 DEBUG - Detailed debugging info</MenuItem>
                <MenuItem value="INFO">ℹ️ INFO - General information</MenuItem>
                <MenuItem value="WARNING">⚠️ WARNING - Warning messages</MenuItem>
                <MenuItem value="ERROR">❌ ERROR - Error messages</MenuItem>
                <MenuItem value="CRITICAL">🚨 CRITICAL - Critical errors only</MenuItem>
              </Select>
            </FormControl>

            {/* Log Type */}
            <FormControl fullWidth>
              <InputLabel>Log Type</InputLabel>
              <Select
                label="Log Type"
                value={config.log_type || 'execution'}
                onChange={(e) => handleConfigChange('log_type', e.target.value)}
              >
                <MenuItem value="execution">⚡ Execution - Node execution events</MenuItem>
                <MenuItem value="data">📊 Data - State and variable data</MenuItem>
                <MenuItem value="performance">⏱️ Performance - Execution time metrics</MenuItem>
                <MenuItem value="error">🚨 Error - Error tracking</MenuItem>
                <MenuItem value="custom">🔧 Custom - User-defined logging</MenuItem>
              </Select>
            </FormControl>

            {/* Message Template */}
            <TextField
              label="Message Template"
              value={config.message_template || 'Node {node_id} executed'}
              onChange={(e) => handleConfigChange('message_template', e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Use {node_id}, {flow_id}, {execution_id} as placeholders"
            />

            {/* Options */}
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.include_state !== false}
                    onChange={(e) => handleConfigChange('include_state', e.target.checked)}
                  />
                )}
                label="Include state data in logs"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.include_timestamps !== false}
                    onChange={(e) => handleConfigChange('include_timestamps', e.target.checked)}
                  />
                )}
                label="Include timestamps"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.log_to_console !== false}
                    onChange={(e) => handleConfigChange('log_to_console', e.target.checked)}
                  />
                )}
                label="Log to console"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.log_to_file === true}
                    onChange={(e) => handleConfigChange('log_to_file', e.target.checked)}
                  />
                )}
                label="Log to file"
              />
            </FormGroup>

            {/* Custom Fields */}
            <TextField
              label="Custom Fields (comma-separated)"
              value={config.custom_fields ? config.custom_fields.join(', ') : ''}
              onChange={(e) => handleConfigChange('custom_fields', e.target.value.split(',').map((s) => s.trim()))}
              fullWidth
              helperText="Additional fields to include: user_id, session_id, etc."
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>How it works:</strong>
                {' '}
                Logger nodes capture and store execution data.
                They can be triggered by connecting them to other nodes or run independently.
                Logs are stored in the execution log for monitoring and debugging.
              </Typography>
            </Alert>
          </Stack>
        );

      case 'debugger':
        return (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              🐛 Debugger Configuration
            </Typography>

            {/* Debug Mode */}
            <FormControl fullWidth>
              <InputLabel>Debug Mode</InputLabel>
              <Select
                label="Debug Mode"
                value={config.debug_mode || 'step'}
                onChange={(e) => handleConfigChange('debug_mode', e.target.value)}
              >
                <MenuItem value="step">👣 Step - Pause at each execution</MenuItem>
                <MenuItem value="breakpoint">🎯 Breakpoint - Pause on condition</MenuItem>
                <MenuItem value="inspect">🔍 Inspect - Inspect without pausing</MenuItem>
                <MenuItem value="trace">📋 Trace - Full execution trace</MenuItem>
              </Select>
            </FormControl>

            {/* Inspection Scope */}
            <FormControl fullWidth>
              <InputLabel>Inspection Scope</InputLabel>
              <Select
                label="Inspection Scope"
                value={config.inspection_scope || 'state'}
                onChange={(e) => handleConfigChange('inspection_scope', e.target.value)}
              >
                <MenuItem value="state">📊 State - Current state only</MenuItem>
                <MenuItem value="variables">🔧 Variables - State variables</MenuItem>
                <MenuItem value="execution">⚡ Execution - Execution path & history</MenuItem>
                <MenuItem value="all">🌟 All - Complete inspection</MenuItem>
              </Select>
            </FormControl>

            {/* Breakpoint Condition */}
            <TextField
              label="Breakpoint Condition (JavaScript)"
              value={config.breakpoint_condition || ''}
              onChange={(e) => handleConfigChange('breakpoint_condition', e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="e.g., status === 'error' or input.success === false"
              placeholder="Leave empty for no breakpoint"
            />

            {/* Auto Continue Settings */}
            <FormControlLabel
              control={(
                <Checkbox
                  checked={config.auto_continue === true}
                  onChange={(e) => handleConfigChange('auto_continue', e.target.checked)}
                />
              )}
              label="Auto-continue after inspection"
            />

            {config.auto_continue && (
              <TextField
                label="Continue Timeout (seconds)"
                type="number"
                value={config.continue_timeout || 30}
                onChange={(e) => handleConfigChange('continue_timeout', parseInt(e.target.value))}
                fullWidth
                helperText="Seconds to wait before auto-continuing"
              />
            )}

            {/* Inspection Options */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              Inspection Options
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.collect_stack_trace !== false}
                    onChange={(e) => handleConfigChange('collect_stack_trace', e.target.checked)}
                  />
                )}
                label="Collect stack trace"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.inspect_variables !== false}
                    onChange={(e) => handleConfigChange('inspect_variables', e.target.checked)}
                  />
                )}
                label="Inspect variables"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.inspect_execution !== false}
                    onChange={(e) => handleConfigChange('inspect_execution', e.target.checked)}
                  />
                )}
                label="Inspect execution path"
              />
            </FormGroup>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ Debug Mode:</strong>
                {' '}
                Debugger nodes will pause execution when triggered.
                Use breakpoints for conditional debugging or step mode for manual inspection.
                Make sure to configure continuation settings to avoid infinite pauses.
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>How it works:</strong>
                {' '}
                Debugger nodes pause execution for inspection.
                Connect them to nodes you want to debug, or they can run independently.
                Use breakpoints for conditional debugging based on state or variables.
              </Typography>
            </Alert>
          </Stack>
        );

      default:
        return (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              ⚙️ Node Configuration
            </Typography>

            <Alert severity="info">
              <Typography variant="body2">
                Configuration options for
                {' '}
                <strong>{type}</strong>
                {' '}
                nodes are not yet implemented.
                Basic functionality should still work.
              </Typography>
            </Alert>

            <TextField
              label="Node Label"
              value={node?.label || ''}
              onChange={(e) => {
                // This would update the node label
                console.log('Label change:', e.target.value);
              }}
              fullWidth
            />
          </Stack>
        );
    }
  };

  const editMode = typeof isEditMode !== 'undefined' ? isEditMode : true;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3 }}>
        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
          Configure
          {' '}
          {node?.label}
          {' '}
          Node
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {node?.type}
          {' '}
          configuration
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 3,
          pb: 3,
          px: 3,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: 4,
          },
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {/* Node Name/Label Section */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Node Configuration
            </Typography>

            <TextField
              label="Node Name/Label"
              value={node?.data?.label || ''}
              onChange={(e) => {
                if (!node.data) node.data = {};
                node.data.label = e.target.value;
                setConfig((c) => ({ ...c }));
              }}
              fullWidth
              placeholder="e.g., Data Analyzer, Content Processor, Form Handler"
              helperText="Give your node a meaningful name. This name can be used to reference the node in loops and other configurations."
              size="medium"
              sx={{ mb: 2 }}
            />

            <Box sx={{
              p: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
            }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                💡 Node Naming Tips:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Use descriptive names like "data_analyzer", "content_processor", "form_handler"
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • Names can be used to reference nodes in loops: ["data_analyzer", "content_processor"]
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                • You can have multiple nodes of the same type with different names
              </Typography>
              <Typography variant="caption" display="block">
                • Leave empty to use the default node ID for referencing
              </Typography>
            </Box>
          </Stack>

          {renderConfigFields()}
          {/* State Variables Section - Available for all nodes */}
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Node State Access
            </Typography>

            {/* Special explanation for form nodes */}
            {node?.type === 'form' && (
              <Box sx={{
                p: 2,
                bgcolor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light',
                mb: 2,
              }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  📋 Form Node State Variables Explained:
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  •
                  {' '}
                  <strong>Form Output Variables</strong>
                  {' '}
                  (above) - Where form submission data is stored
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  •
                  {' '}
                  <strong>Node Input/Output Variables</strong>
                  {' '}
                  (below) - What the node can read/write during execution
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  • For
                  {' '}
                  <strong>dynamic forms</strong>
                  , include
                  {' '}
                  <code>form_fields_json</code>
                  {' '}
                  in Node Input/Output Variables
                </Typography>
                <Typography variant="caption" display="block">
                  • For
                  {' '}
                  <strong>static forms</strong>
                  , you only need Form Output Variables
                </Typography>
              </Box>
            )}
            <TextField
              label="Node Input/Output Variables (comma separated)"
              value={(node?.data?.stateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                if (!node.data) node.data = {};
                node.data.stateVars = value;
                setConfig((c) => ({ ...c }));
              }}
              fullWidth
              placeholder="e.g. input, output, form_fields_json, dynamic_fields"
              helperText="State variables this node can read from or write to during execution. For dynamic forms, include the variable containing form field definitions (e.g., form_fields_json)."
            />
            {/* Optionally, you could add a node-level variable field here, but de-emphasize it or comment it out for now. */}
            {/*
            <TextField
              label="Node State Variables (rarely needed)"
              value={(node?.data?.nodeStateVars || []).join(', ')}
              onChange={(e) => {
                const value = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                if (!node.data) node.data = {};
                node.data.nodeStateVars = value;
                setConfig((c) => ({ ...c }));
              }}
              fullWidth
              placeholder="(advanced)"
              helperText="Node-local variables (not shared). Use only if you know you need this."
              sx={{ mt: 2, display: 'none' }}
            />
            */}

            {/* Quick State Variable Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentVars = node?.data?.stateVars || [];
                  if (!node.data) node.data = {};
                  node.data.stateVars = [...currentVars, 'input'];
                  setConfig((c) => ({ ...c }));
                }}
              >
                Add 'input'
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentVars = node?.data?.stateVars || [];
                  if (!node.data) node.data = {};
                  node.data.stateVars = [...currentVars, 'form_fields_json'];
                  setConfig((c) => ({ ...c }));
                }}
              >
                Add 'form_fields_json'
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentVars = node?.data?.stateVars || [];
                  if (!node.data) node.data = {};
                  node.data.stateVars = [...currentVars, 'dynamic_fields'];
                  setConfig((c) => ({ ...c }));
                }}
              >
                Add 'dynamic_fields'
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentVars = node?.data?.stateVars || [];
                  if (!node.data) node.data = {};
                  node.data.stateVars = [...currentVars, 'form_data'];
                  setConfig((c) => ({ ...c }));
                }}
              >
                Add 'form_data'
              </Button>
            </Box>

            {/* State Variable Usage Examples */}
            <Box sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                💡 Form Node State Variable Examples:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>input</strong>
                {' '}
                - Read from previous node's output
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>form_fields_json</strong>
                {' '}
                - Dynamic form field definitions (for dynamic forms)
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>dynamic_fields</strong>
                {' '}
                - Alternative name for dynamic form fields
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                •
                {' '}
                <strong>form_data</strong>
                {' '}
                - Store form submission data
              </Typography>
              <Typography variant="caption" display="block">
                •
                {' '}
                <strong>user_input</strong>
                {' '}
                - Store user's form input
              </Typography>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NodeConfigDialog;
